import { createAnchor, getPositionFromIndex } from "../components/AutoSuggestion";
import { findTabularEnvs, parseColumnSpec, findCommandorNot, capitalizeInsideBraces, isCommented, getallData, getInsideTextofBraces, createGranularAnchors, isLineCommented, LineCommented, buildStyleContext, makeAnchorEmitter } from "../components/ReusableFunctions";
import { getOverlappingAnchors, isAlreadyAnchored, isRejectedByLivePosition } from "../components/AutoSuggestion";
import { eplStyles } from "./commonstyles";
const _CITATION_KW_MAP = {
    // Figure variants
    'Figures': 'figs.', 'Figure': 'fig.',
    'Figs.': 'figs.', 'Figs': 'figs.',
    'Fig.': 'fig.', 'Fig': 'fig.',
    // Table variants
    'Tables': 'tables', 'Table': 'table',
    'Tabs.': 'tables', 'Tabs': 'tables',
    'Tab.': 'table', 'Tab': 'table',
    // Equation variants
    'Equations': 'eqs.', 'Equation': 'eq.',
    'Eqs.': 'eqs.', 'Eqs': 'eqs.',
    'Eq.': 'eq.', 'Eq': 'eq.',
};

// Build sorted keyword list (longest first → greedy, avoids partial matches)
const _CITATION_KW_SORTED = Object.keys(_CITATION_KW_MAP)
    .sort((a, b) => b.length - a.length);

// Helper: build a regex for a subset of keywords
function _buildCitationPattern(keys) {
    const escaped = keys.map(k => k.replace(/\./g, '\\.')).join('|');
    // (?<![a-zA-Z]) — not preceded by a letter (avoids matching inside words)
    // (?![a-zA-Z])  — not followed by a letter (avoids truncating longer words)
    return new RegExp(`(?<![a-zA-Z])(${escaped})(?![a-zA-Z])`, 'g');
}

// Helper: balanced-parenthesis extraction starting at openIdx (the '(')
function _extractBalancedParen(text, openIdx) {
    let count = 0, i = openIdx, result = '';
    while (i < text.length) {
        const ch = text[i];
        result += ch;
        if (ch === '(') count++;
        else if (ch === ')') { count--; if (count === 0) break; }
        i++;
    }
    return result; // full "(....)" string
}

// Helper: apply keyword lowercasing inside a parenthesised citation.
// Only acts if the paren block contains at least one \ref or \eqref.
function _applyCitationKeywords(parenContent, kwPat, kwMap) {
    if (!/\\(?:ref|eqref)\{/.test(parenContent)) return parenContent;
    kwPat.lastIndex = 0;
    return parenContent.replace(kwPat, (_, kw) => kwMap[kw] ?? kw);
}


export const epl = {

    PreambleInsertion: {
        name: "Insert Preamble Before Title",
        description: "Automatically inserts default preamble before the first \\title{}",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/preambleBefore.png",
        afterImage: "/images/description/preambleAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            // Use a clean template without leading spaces on each line
            const preamble = [
                "\\documentclass[doublecol,final]{epl2}",
                "",
                "\\usepackage{amsmath}",
                "\\usepackage{amssymb}",
                "\\usepackage{multirow}",
                "\\usepackage{mathrsfs}",
                "",
                "%\\usepackage{showframe}",
                "%\\usepackage[allfiguresdraft]{draftfigure}",
                "",
                "\\usepackage{xcolor}",
                "",
                "\\usepackage[bookmarks=false]{hyperref}",
                "",
                "\\hypersetup{",
                "colorlinks=false,",
                "pdfborder={0 0 0},",
                "}",
                "",
                "\\makeatletter",
                "\\setlength\\arraycolsep{2pt}",
                "\\makeatother",
                "",
                "\\vol{xx}",
                "\\issue{xx}",
                "\\year{2026}",
                "\\copyyear{2026}",
                "\\firstpage{1}",
                "\\doi{10.1209/0295-5075/xx/xxxxx}",
                "\\pgid{xxxxx}",
                "\\received{}",
                "\\acceptedinfinalform{}",
                "\\paperpub{Xxxx xxxx}",
                "\\onlinepub{xx Xxxx xxxx}"
            ].join('\n');

            const staleIds = new Set();
            const titlePattern = /\\title\s*\{/i;
            const match = titlePattern.exec(texFile);
            if (!match) return staleIds;

            // Compare content above title to see if we even need to suggest
            const textAboveTitle = texFile.slice(0, match.index);
            const normalize = (str) => str.replace(/\s+/g, ' ').trim();
            if (normalize(textAboveTitle) === normalize(preamble)) return staleIds;

            // FIX: Anchor from index 0 to the start of \title{
            // This ensures all previous content/whitespace is replaced cleanly
            // const position = {
            //     startRow: 0,
            //     startCol: 0,
            //     endRow: getPositionFromIndex(texFile, match.index).startRow,
            //     endCol: getPositionFromIndex(texFile, match.index).startCol,
            // };
            const position = getPositionFromIndex(texFile, match.index, match[0].length);

            if (isRejectedByLivePosition(position, this.name, textAboveTitle)) return staleIds;

            const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
            if (overlapping.length > 0) {
                overlapping.forEach(old => staleIds.add(old.id));
            } else {
                if (isAlreadyAnchored(position, this.name, existingSuggestions)) return staleIds;
            }

            createAnchor(
                session, Range,
                position,
                textAboveTitle, // Replace everything above the title
                preamble + '\n\n', // With the clean preamble
                this.markerClass,
                this.name,
                this.description,
                anchorlist,
                {
                    isPreambleInsertion: true,
                    textAboveTitle
                }
            );

            return staleIds;
        }
    },

    removeunwantedtext: {
        name: "Remove \\textcolor{black}",
        description: "Removes the text \\textcolor{black} throughout the body of the document",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/RemoveTextBefore.png",
        afterImage: "/images/description/RemoveTextAfter.png",
        execute: function (texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = []) {
            const staleIds = new Set();
            if (!texFile) return staleIds;

            const pattern = /\\textcolor\{black\}/g;
            pattern.lastIndex = 0; // Reset regex to ensure it finds all instances on every check

            let match;
            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = ""; // Remove by replacing with empty string
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                // --- REFRESH / OVERLAP LOGIC ---
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);

                if (overlapping.length > 0) {
                    // Mark old versions for cleanup to avoid duplicate markers
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    // Skip if the user has already rejected this removal or it's already anchored
                    if (isAlreadyAnchored(position, this.name, existingSuggestions, fullMatch)) continue;
                    if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;
                }

                // Standard check to ignore commented-out code
                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(
                        session,
                        Range,
                        position,
                        fullMatch,
                        suggestion,
                        this.markerClass,
                        this.name,
                        this.description,
                        anchorlist
                    );
                }
            }
            return staleIds;
        }
    },

    TitleLowerCase: {
        name: "Title Case",
        description: "Capitalize first letter of each word in \\title{}, preserve math and LaTeX commands, skip certain small words. Capitalize after periods.",
        markerClass: "ace-Common-highlight",
        execute: function (texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = []) {
            const staleIds = new Set();
            if (!texFile) return staleIds;

            const titlePattern = /\\title\s*/gi;
            titlePattern.lastIndex = 0; // Essential for consistent matching during typing

            let match;
            while ((match = titlePattern.exec(texFile)) !== null) {
                const capturedBracedData = texFile.slice(match.index + match[0].length);
                const capturedText = getInsideTextofBraces(capturedBracedData);

                if (!capturedText) continue;

                const innerContent = capturedText.slice(1, -1);
                // capitalizeInsideBraces handles the logic for small words, math, and periods
                const finalContent = capitalizeInsideBraces(innerContent, true);

                if (finalContent !== innerContent) {
                    const fullMatch = `\\title${capturedText}`;
                    const suggestion = `\\title{${finalContent}}`;
                    const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                    // --- REFRESH / OVERLAP LOGIC ---
                    const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);

                    if (overlapping.length > 0) {
                        // Mark for refresh so the UI updates to the new capitalized string
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        // Check if user already saw/rejected this specific casing
                        if (isAlreadyAnchored(position, this.name, existingSuggestions, fullMatch)) continue;
                        if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;
                    }

                    // Avoid highlighting inside comments
                    if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                        createAnchor(
                            session,
                            Range,
                            position,
                            fullMatch,
                            suggestion,
                            this.markerClass,
                            this.name,
                            this.description,
                            anchorlist
                        );
                    }
                }
            }
            return staleIds;
        }
    },
    shortTitle: {
        name: "Capitalize \\shorttitle",
        description: "Capitalizes \\shorttitle tag (Sentence case), preserves math. If no \\shorttitle, adds one from \\title content.",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/shortTitleBefore.png",
        afterImage: "/images/description/shortTitleAfter.png",
        execute: function (texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = []) {
            const staleIds = new Set();
            if (!texFile) return staleIds;

            // --- Helper: first letter capital, rest lowercase, preserve $math$ ---
            function formatTitle(innerContent) {
                const parts = innerContent.split(/(\$[^$]*\$)/g);
                let firstCharDone = false;
                let finalContent = "";
                for (let i = 0; i < parts.length; i++) {
                    const part = parts[i];
                    if (part.startsWith("$") && part.endsWith("$")) {
                        finalContent += part;
                        continue;
                    }
                    for (let j = 0; j < part.length; j++) {
                        const ch = part[j];
                        if (!firstCharDone && /\S/.test(ch)) {
                            finalContent += ch.toUpperCase();
                            firstCharDone = true;
                        } else {
                            finalContent += ch.toLowerCase();
                        }
                    }
                }
                return finalContent;
            }

            const hasShortTitle = /\\shorttitle\s*/i.test(texFile);

            // --- CASE 1: \shorttitle exists → fix its capitalization ---
            if (hasShortTitle) {
                const shorttitlePattern = /\\shorttitle\s*/gi;
                shorttitlePattern.lastIndex = 0;
                let match;
                while ((match = shorttitlePattern.exec(texFile)) !== null) {
                    const capturedBracedData = texFile.slice(match.index + match[0].length);
                    const capturedText = getInsideTextofBraces(capturedBracedData);
                    if (!capturedText) continue;

                    const innerContent = capturedText.slice(1, -1);
                    const finalContent = formatTitle(innerContent);

                    if (finalContent !== innerContent) {
                        const fullMatch = `\\shorttitle${capturedText}`;
                        const suggestion = `\\shorttitle{${finalContent}}`;
                        const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                        // REFRESH LOGIC
                        const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                        if (overlapping.length > 0) {
                            overlapping.forEach(old => staleIds.add(old.id));
                        } else {
                            if (isAlreadyAnchored(position, this.name, existingSuggestions, fullMatch)) continue;
                            if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;
                        }

                        if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                            createAnchor(session, Range, position, fullMatch, suggestion, this.markerClass, this.name, this.description, anchorlist);
                        }
                    }
                }
                return staleIds;
            }

            // --- CASE 2: No \shorttitle → find \title and add \shorttitle after it ---
            const titlePattern = /\\title\s*/gi;
            titlePattern.lastIndex = 0;
            let tMatch;

            while ((tMatch = titlePattern.exec(texFile)) !== null) {
                const capturedBracedData = texFile.slice(tMatch.index + tMatch[0].length);
                const capturedText = getInsideTextofBraces(capturedBracedData);
                if (!capturedText) continue;

                const innerContent = capturedText.slice(1, -1);
                const formattedContent = formatTitle(innerContent);

                const originalFull = `\\title${capturedText}`;
                const suggestion = `${originalFull}\n\n\\shorttitle{${formattedContent}}`;
                const position = getPositionFromIndex(texFile, tMatch.index, originalFull.length);

                // REFRESH LOGIC
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions, originalFull)) continue;
                    if (isRejectedByLivePosition(position, this.name, originalFull)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(
                        session, Range, position,
                        originalFull, suggestion,
                        this.markerClass, this.name,
                        "Add \\shorttitle from \\title content",
                        anchorlist
                    );
                }
            }

            return staleIds;
        }
    },
    ChemInsertion: {
        name: "Insert \\chem in title tag",
        description: "Replaces chemical notations (e.g., H$_2$O) in the \\title tag with \\chem{}",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/ChemInsertionBefore.png",
        afterImage: "/images/description/ChemInsertionAfter.png",
        execute: function (texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = []) {
            const staleIds = new Set();
            if (!texFile) return staleIds;

            const titlePattern = /\\title\s*/gi;
            titlePattern.lastIndex = 0;

            let match;
            while ((match = titlePattern.exec(texFile)) !== null) {
                const capturedBracedData = texFile.slice(match.index + match[0].length);
                const capturedText = getInsideTextofBraces(capturedBracedData);

                if (!capturedText) continue;

                let innerContent = capturedText.slice(1, -1);

                /**
                 * Regex Explanation:
                 * ([A-Z][a-z]?)  -> Captures an element (e.g., H, Li, O)
                 * \$_\{?(\d+)\}?\$ -> Captures math subscript (e.g., $_2$ or $_{10}$)
                 * ([A-Z][a-z]*)? -> Captures optional trailing elements (e.g., the 'O' in H$_2$O)
                 */
                const chemRegex = /([A-Z][a-z]?)\$_\{?(\d+)\}?\$([A-Z][a-z]*)?/g;

                let newContent = innerContent.replace(chemRegex, (m, e1, sub, e2) => {
                    const trailing = e2 ? e2 : "";
                    return `\\chem{${e1}_{${sub}}${trailing}}`;
                });

                if (innerContent !== newContent) {
                    const fullMatch = `\\title${capturedText}`;
                    const finalSuggestion = `\\title{${newContent}}`;
                    const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                    const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);

                    if (overlapping.length > 0) {
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        if (isAlreadyAnchored(position, this.name, existingSuggestions, fullMatch)) continue;
                        if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;
                    }

                    if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                        createAnchor(
                            session,
                            Range,
                            position,
                            fullMatch,
                            finalSuggestion,
                            this.markerClass,
                            this.name,
                            this.description,
                            anchorlist
                        );
                    }
                }
            }
            return staleIds;
        }
    },
    MathInsertion: {
        name: "Insert \\mth in title tag",
        description: "Replaces the $ signs in the \\title tag with \\mth{}",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/MathInsertionBefore.png",
        afterImage: "/images/description/MathInsertionAfter.png",
        execute: function (texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = []) {
            const staleIds = new Set();
            if (!texFile) return staleIds;

            const titlePattern = /\\title\s*/gi;
            titlePattern.lastIndex = 0; // Reset for repeated execution

            let match;
            while ((match = titlePattern.exec(texFile)) !== null) {
                const capturedBracedData = texFile.slice(match.index + match[0].length);
                const capturedText = getInsideTextofBraces(capturedBracedData);

                if (!capturedText) continue;

                let innerContent = capturedText.slice(1, -1);
                // Replaces $...$ with \mth{...}
                let newContent = innerContent.replace(/\$(.*?)\$/gs, (m, inner) => {
                    return `\\mth{${inner}}`;
                });

                // Only proceed if a replacement actually happened
                if (innerContent !== newContent) {
                    const fullMatch = `\\title${capturedText}`;
                    const finalSuggestion = `\\title{${newContent}}`;
                    const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                    // --- REFRESH / OVERLAP LOGIC ---
                    const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);

                    if (overlapping.length > 0) {
                        // Mark existing anchor for replacement to prevent flickering
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        // Logic to ignore already accepted/rejected spots
                        if (isAlreadyAnchored(position, this.name, existingSuggestions, fullMatch)) continue;
                        if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;
                    }

                    // Final check to ensure we aren't in a commented line
                    if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                        createAnchor(
                            session,
                            Range,
                            position,
                            fullMatch,
                            finalSuggestion,
                            this.markerClass,
                            this.name,
                            this.description,
                            anchorlist
                        );
                    }
                }
            }
            return staleIds;
        }
    },
    authorNaming: {
        name: "Author Initials Formatting",
        description: "Move author initials to the back (e.g., Tiribocchi A.)",
        markerClass: "ace-Common-highlight",

        execute: function (texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = []) {
            const staleIds = new Set();
            if (!texFile) return staleIds;

            try {
                const authorPattern = /\\author\s*\{/g;
                let m;

                while ((m = authorPattern.exec(texFile)) !== null) {
                    let depth = 1, i = m.index + m[0].length, content = '';
                    while (i < texFile.length && depth > 0) {
                        if (texFile[i] === '{') depth++;
                        else if (texFile[i] === '}') { depth--; if (depth === 0) break; }
                        content += texFile[i]; i++;
                    }
                    const fullMatch = texFile.slice(m.index, i + 1);

                    // Split authors by \and or commas outside braces
                    let authors = content.split(/\\and|,(?![^{]*\})/gi);
                    let needsChange = false;

                    let modifiedAuthors = authors.map(authorStr => {
                        let trimmed = authorStr.trim();

                        /**
                         * IMPROVED REGEX:
                         * Group 1: Matches initials like "A." or "A. B." at start (space optional)
                         * Group 3: Matches the Surname (stops at space, \, or { )
                         */
                        const initialsLeadPattern = /^(([A-Z]\.\s*)+)\s*([^\\{}\s,]+)/;
                        const match = trimmed.match(initialsLeadPattern);

                        if (match) {
                            needsChange = true;
                            // Clean initials to ensure they have a dot and space (e.g., "A. ")
                            let initials = match[1].trim();
                            const surname = match[3].trim();

                            // Replace "A.Tiribocchi" with "Tiribocchi A."
                            // We use a template to ensure a space exists between Surname and Initial
                            return trimmed.replace(match[0], `${surname} ${initials}`);
                        }
                        return authorStr;
                    });

                    if (needsChange) {
                        const finalSuggestion = `\\author{${modifiedAuthors.join(' \\and ')}}`;
                        const position = getPositionFromIndex(texFile, m.index, fullMatch.length);

                        const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                        if (overlapping.length > 0) {
                            overlapping.forEach(old => staleIds.add(old.id));
                        } else {
                            if (isAlreadyAnchored(position, this.name, existingSuggestions, fullMatch)) continue;
                            if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                            createAnchor(
                                session, Range, position,
                                fullMatch, finalSuggestion,
                                this.markerClass, this.name, this.description, anchorlist
                            );
                        }
                    }
                }
            } catch (err) {
                console.error("Error in authorNaming style:", err);
            }

            return staleIds;
        }
    },
    AuthorFormatting: {
        name: "Author Formatting",
        description: "Add thanks/orcid to the first author and join all authors with \\and",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = []) {
            const staleIds = new Set();
            if (!texFile) return staleIds;

            const authorPattern = /\\author\s*\{/g;
            let m;
            while ((m = authorPattern.exec(texFile)) !== null) {
                // --- 1. Extract Bracketed Content ---
                let depth = 1, i = m.index + m[0].length, content = '';
                while (i < texFile.length && depth > 0) {
                    if (texFile[i] === '{') depth++;
                    else if (texFile[i] === '}') { depth--; if (depth === 0) break; }
                    content += texFile[i]; i++;
                }

                const fullMatch = texFile.slice(m.index, i + 1);

                // --- 2. Safety Check ---
                // Skip if \thanks is already present to prevent duplicate suggestions
                if (content.includes('\\thanks')) continue;

                // --- 3. Robust Splitting ---
                /**
                 * Regex Explanation:
                 * \\\\and       -> Matches the LaTeX \and command
                 * |             -> OR
                 * ,(?![^{]*\})  -> Matches a comma ONLY if it is not inside { } 
                 * (Prevents breaking \inst{1,2})
                 */
                let authors = content.split(/\\and|,(?![^{]*\})/gi)
                    .map(name => name.trim())
                    .filter(name => name.length > 0);

                if (authors.length === 0) continue;

                // --- 4. Apply Formatting Rules ---
                const thanksContent = `\\thanks{E-mail: \\email{} (corresponding author)}\\orcid{http://orcid.org/}`;

                // Attach thanks/orcid to the first author only
                authors[0] = `${authors[0]}${thanksContent}`;

                // Rejoin everyone using the standard LaTeX \and
                let newAuthorContent = authors.join(' \\and ');

                const finalSuggestion = `\\author{${newAuthorContent}}`;

                // --- 5. Suggestion & Anchor Logic ---
                if (fullMatch !== finalSuggestion) {
                    const position = getPositionFromIndex(texFile, m.index, fullMatch.length);
                    const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);

                    if (overlapping.length > 0) {
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        if (isAlreadyAnchored(position, this.name, existingSuggestions, fullMatch)) continue;
                        if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;
                    }

                    createAnchor(
                        session, Range, position,
                        fullMatch, finalSuggestion,
                        this.markerClass, this.name, this.description, anchorlist
                    );
                }
            }
            return staleIds;
        }
    },
    shortAuthor: {
        name: "Format \\shortauthor",
        description: "Extracts names from \\author, handles 'and' for two, or '\\textit{et al}.' for three+",
        markerClass: "ace-Common-highlight",

        execute: function (texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = []) {
            const staleIds = new Set();
            if (!texFile) return staleIds;

            // --- Helper: Clean LaTeX commands out to get raw names ---
            function getCleanedNames(content) {
                let cleaned = content;
                const commandsToRemove = ['\\thanks', '\\orcid', '\\inst', '\\email'];

                commandsToRemove.forEach(cmd => {
                    let idx;
                    while ((idx = cleaned.indexOf(cmd)) !== -1) {
                        const bracePart = getInsideTextofBraces(cleaned.slice(idx + cmd.length));
                        if (bracePart) {
                            cleaned = cleaned.replace(cmd + bracePart, '');
                        } else {
                            cleaned = cleaned.replace(cmd, '');
                        }
                    }
                });

                const names = cleaned.split(/\\and|,/g)
                    .map(n => n.trim())
                    .filter(n => n.length > 0);

                if (names.length === 1) return names[0];
                if (names.length === 2) return `${names[0]} and ${names[1]}`;
                if (names.length >= 3) return `${names[0]} \\textit{et al}.`;
                return "";
            }

            const authorPattern = /\\author\s*\{/g;
            const authorEntries = [];
            let aMatch;
            // Reset lastIndex just in case
            authorPattern.lastIndex = 0;

            while ((aMatch = authorPattern.exec(texFile)) !== null) {
                // FIXED LINE HERE
                const braceData = getInsideTextofBraces(texFile.slice(aMatch.index + aMatch[0].length - 1));
                if (braceData) {
                    authorEntries.push({
                        full: `\\author${braceData}`,
                        content: braceData.slice(1, -1),
                        index: aMatch.index
                    });
                }
            }

            const hasShortAuthor = /\\shortauthor/i.test(texFile);

            if (hasShortAuthor) {
                const shortPattern = /\\shortauthor\s*\{/gi;
                let sMatch;
                while ((sMatch = shortPattern.exec(texFile)) !== null) {
                    const capturedBracedData = getInsideTextofBraces(texFile.slice(sMatch.index + sMatch[0].length - 1));
                    if (!capturedBracedData) continue;

                    const currentShortContent = capturedBracedData.slice(1, -1);
                    const sourceContent = authorEntries.map(e => e.content).join(' \\and ');
                    const formatted = getCleanedNames(sourceContent || currentShortContent);

                    if (formatted && currentShortContent !== formatted) {
                        const originalFull = `\\shortauthor${capturedBracedData}`;
                        const suggestion = `\\shortauthor{${formatted}}`;
                        const pos = getPositionFromIndex(texFile, sMatch.index, originalFull.length);

                        const overlapping = getOverlappingAnchors(pos, this.name, existingSuggestions);
                        if (overlapping.length > 0) {
                            overlapping.forEach(old => staleIds.add(old.id));
                        } else {
                            if (isAlreadyAnchored(pos, this.name, existingSuggestions, originalFull)) continue;
                            createAnchor(session, Range, pos, originalFull, suggestion, this.markerClass, this.name, this.description, anchorlist);
                        }
                    }
                }
            }
            else if (authorEntries.length > 0) {
                const lastAuthor = authorEntries[authorEntries.length - 1];
                const sourceContent = authorEntries.map(e => e.content).join(' \\and ');
                const formatted = getCleanedNames(sourceContent);

                if (formatted) {
                    const suggestion = `${lastAuthor.full}\n\\shortauthor{${formatted}}`;
                    const pos = getPositionFromIndex(texFile, lastAuthor.index, lastAuthor.full.length);

                    if (!isAlreadyAnchored(pos, this.name, existingSuggestions, lastAuthor.full)) {
                        createAnchor(session, Range, pos, lastAuthor.full, suggestion, this.markerClass, this.name, "Add missing \\shortauthor", anchorlist);
                    }
                }
            }

            return staleIds;
        }
    },
    FormatAnd: {
        name: "Add \\and in \\author",
        description: "Replaces 'and' in the \\author tag with \\and if not already escaped",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/FormatandBefore.png",
        afterImage: "/images/description/FormatandAfter.png",
        execute: function (texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = []) {
            const staleIds = new Set();
            if (!texFile) return staleIds;

            const authorPattern = /\\author(\[[^\]]*\])?\s*/gi;
            authorPattern.lastIndex = 0; // Reset regex state

            let match;
            while ((match = authorPattern.exec(texFile)) !== null) {
                const capturedBracedData = texFile.slice(match.index + match[0].length);
                const capturedText = getInsideTextofBraces(capturedBracedData);

                if (!capturedText) continue;

                const innerContent = capturedText.slice(1, -1);
                // Matches " and " or ", and " but not "\and"
                const newContent = innerContent.replace(
                    /(?<!\\)(?<=\W),?\s?and(?=\W)/g,
                    " \\and "
                );

                // Only proceed if a replacement actually happened
                if (innerContent !== newContent) {
                    const fullMatch = match[0] + capturedText;
                    const finalSuggestion = `\\author${match[1] || ""}{${newContent}}`;
                    const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                    // --- REFRESH / OVERLAP LOGIC ---
                    const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);

                    if (overlapping.length > 0) {
                        // Mark for refresh
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        // Standard guard checks
                        if (isAlreadyAnchored(position, this.name, existingSuggestions, fullMatch)) continue;
                        if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;
                    }

                    // Ensure we aren't in a comment
                    if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                        createAnchor(
                            session,
                            Range,
                            position,
                            fullMatch,
                            finalSuggestion,
                            this.markerClass,
                            this.name,
                            this.description,
                            anchorlist
                        );
                    }
                }
            }
            return staleIds;
        }
    },
    AddInstituteTag: {
        name: "Address/Affiliation to \\inst{}",
        description: "Modifies address/affiliation into \\inst{} and wraps them in \\institute{}",
        markerClass: "ace-Common-highlight",
        execute: function (texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = []) {
            const staleIds = new Set();
            if (!texFile) return staleIds;

            // --- CASE 1: \institute already exists (Formatting Check) ---
            if (/\\institute/.test(texFile)) {
                const institutePattern = /\\institute\s*/gi;
                let match;
                while ((match = institutePattern.exec(texFile)) !== null) {
                    const capturedBracedData = texFile.slice(match.index + match[0].length);
                    const capturedText = getInsideTextofBraces(capturedBracedData);

                    if (capturedText) {
                        const innerContent = capturedText.slice(1, -1);
                        const suggestion = `\\institute{\n${innerContent}\n}`;
                        const fullMatch = match[0] + capturedText;
                        const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                        // --- REFRESH LOGIC ---
                        const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                        if (overlapping.length > 0) {
                            overlapping.forEach(old => staleIds.add(old.id));
                        } else {
                            if (isAlreadyAnchored(position, this.name, existingSuggestions, fullMatch)) continue;
                            if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;
                        }

                        if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                            createAnchor(session, Range, position, fullMatch, suggestion, this.markerClass, this.name, this.description, anchorlist);
                        }
                    }
                }
                return staleIds;
            }

            // --- CASE 2: Convert \address / \affiliation to \institute ---
            if (/(\\affiliation|\\address|\\affili)/.test(texFile)) {
                const affPattern = /(\\affiliation|\\address|\\affili)\s*/gi;
                const instValues = [];
                const affMatches = [];
                let match;

                while ((match = affPattern.exec(texFile)) !== null) {
                    const capturedBracedData = texFile.slice(match.index + match[0].length);
                    const capturedText = getInsideTextofBraces(capturedBracedData);

                    if (capturedText) {
                        const innerContent = capturedText.slice(1, -1);
                        instValues.push(innerContent);
                        affMatches.push({
                            index: match.index,
                            fullMatch: match[0] + capturedText,
                            length: match[0].length + capturedText.length
                        });
                    }
                }

                if (instValues.length === 0 || affMatches.length === 0) return staleIds;

                const instituteLines = instValues.map((value, idx) => `\\inst{${idx + 1}} ${value}`);
                const newInstitute = `\\institute{\n${instituteLines.join("\n")}\n}`;

                const firstAff = affMatches[0];
                const lastAff = affMatches[affMatches.length - 1];
                const startIndex = firstAff.index;
                const endIndex = lastAff.index + lastAff.length;
                const originalText = texFile.slice(startIndex, endIndex);

                const position = getPositionFromIndex(texFile, startIndex, originalText.length);

                // --- REFRESH LOGIC FOR CONVERSION ---
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions, originalText)) return staleIds;
                    if (isRejectedByLivePosition(position, this.name, originalText)) return staleIds;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, originalText, newInstitute, this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },
    footNote: {
        name: "Remove space before \\footnote",
        description: "Removes any whitespace (spaces or tabs) before the \\footnote tag.",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/footNoteBefore.png",
        afterImage: "/images/description/footNoteAfter.png",
        execute: function (texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = []) {
            const staleIds = new Set();

            // Regex: matches 1 or more spaces/tabs followed by \footnote{...}
            // Note: handles nested braces slightly more robustly with [^{}]*
            const footnotePattern = /[ \t]+\\footnote\{((?:[^{}]*\{[^{}]*\})*[^{}]*)\}/g;
            footnotePattern.lastIndex = 0;

            let match;
            while ((match = footnotePattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const content = match[1];

                const suggestion = `\\footnote{${content}}`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                // --- REFRESH / OVERLAP LOGIC ---
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);

                if (overlapping.length > 0) {
                    // Mark old versions as stale so they are redrawn
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    // Standard guards for new suggestions
                    if (isAlreadyAnchored(position, this.name, existingSuggestions, fullMatch)) continue;
                    if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;
                }

                // Ensure we aren't matching inside a comment or another command
                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(
                        session,
                        Range,
                        position,
                        fullMatch,
                        suggestion,
                        this.markerClass,
                        this.name,
                        this.description,
                        anchorlist
                    );
                }
            }

            return staleIds;
        }
    },

    SIUnitStyling: {
        name: "SIUnit with \\un{}",
        description: "Wraps SI units in \\un{} and removes space between number and unit.",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = [], rejectedSuggestions = []) {
            const staleIds = new Set();

            const context = buildStyleContext({
                texFile, session, Range, anchorlist, existingSuggestions, staleIds,
                styleName: this.name,
                markerClass: this.markerClass,
                scope: { wholeDocument: true },
                excludeMath: false // We need to see units inside $ $
            });

            const baseUnits = [
                'm', 'g', 'kg', 's', 'A', 'K', 'mol', 'cd',
                'Hz', 'N', 'Pa', 'J', 'W', 'C', 'V', 'F', 'Ω', 'S', 'Wb', 'T', 'H',
                'rad', 'sr', 'lm', 'lx', 'Bq', 'Gy', 'Sv', 'kat',
                'mm', 'cm', 'km', 'mg', 'μg', 'ng', 'ms', 'μs', 'ns', 'mA', 'μA',
                'kHz', 'MHz', 'GHz', 'mV', 'kV', 'MV', 'mW', 'kW', 'MW',
                'eV', 'keV', 'MeV', 'GeV', 'L', 'mL', 'dB', 'u', 'Da', 'kDa'
            ];

            const unitPattern = baseUnits.join('|');
            const compoundUnitPattern = `(?:${unitPattern})(?:[\\\\/·\\\\.](?:${unitPattern}))*`;

            const siUnitRegex = new RegExp(`(?<!\\\\un\\{)([^\\w\\\\])(\\d+(?:\\.\\d+)?)\\s*~*\\s*(${compoundUnitPattern})(?![\\w])`, 'gm');

            context.segments.forEach(seg => {
                let match;
                siUnitRegex.lastIndex = 0;

                while ((match = siUnitRegex.exec(seg.text)) !== null) {
                    const fullMatch = match[0];
                    const prefix = match[1];
                    const number = match[2];
                    const unit = match[3];

                    const suggestion = `${prefix}${number}\\un{${unit}}`;

                    if (fullMatch !== suggestion) {
                        seg.emit(
                            fullMatch,
                            suggestion,
                            match.index,
                            this.description
                        );
                    }
                }
            });

            return staleIds;
        }
    },

    // BeginEquation: {
    //     name: "\\begin{equation}",
    //     description: "Fix wrong labels in equations, add missing labels, auto-update all refs on accept",
    //     markerClass: "ace-Common-highlight",

    //     execute(texFile, session, Range, anchorlist,
    //         fileName, Allfiles,
    //         existingSuggestions = [],
    //         rejectedSuggestions = []) {

    //         const staleIds = new Set();
    //         const filename = (fileName || "doc").replace(/\.tex$/i, '');

    //         // ── Helpers ────────────────────────────────────────────────────────────────

    //         function isCommented(text, index) {
    //             let lineStart = index;
    //             while (lineStart > 0 && text[lineStart - 1] !== '\n') lineStart--;
    //             for (let i = lineStart; i < index; i++) {
    //                 if (text[i] === '%' && (i === 0 || text[i - 1] !== '\\')) return true;
    //             }
    //             return false;
    //         }

    //         function findAllLabels(text) {
    //             const results = [];
    //             const pat = /\\label\s*\{([^}]*?\S[^}]*?)\}/g;
    //             let m;
    //             while ((m = pat.exec(text)) !== null) {
    //                 if (!isCommented(text, m.index))
    //                     results.push({ fullText: m[0], inner: m[1], index: m.index });
    //             }
    //             return results;
    //         }

    //         function emitSuggestion(originalText, suggestionText, absIndex, description, extraStyleInfo = {}) {
    //             const position = getPositionFromIndex(texFile, absIndex, originalText.length);
    //             if (isRejectedByLivePosition(position, this.name, originalText)) return;
    //             const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
    //             if (overlapping.length > 0) {
    //                 overlapping.forEach(old => staleIds.add(old.id));
    //             } else {
    //                 if (isAlreadyAnchored(position, this.name, existingSuggestions)) return;
    //             }
    //             if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
    //                 createAnchor(session, Range, position,
    //                     originalText, suggestionText,
    //                     this.markerClass, this.name,
    //                     description, anchorlist,
    //                     extraStyleInfo);
    //             }
    //         }
    //         function fixLabel(labelText, correctLabel, absLabelIndex) {
    //             const expected = `\\label{${correctLabel}}`;
    //             if (labelText === expected) return;

    //             const oldKeyMatch = /\\label\s*\{([^}]*)\}/.exec(labelText);
    //             if (!oldKeyMatch) return;
    //             const oldKey = oldKeyMatch[1];
    //             const newKey = correctLabel;
    //             emitSuggestion.call(this,
    //                 labelText, expected, absLabelIndex,
    //                 `Fix label → \\label{${newKey}}`,
    //                 {
    //                     isLabelFix: true,
    //                     oldKey,
    //                     newKey,
    //                 }
    //             );
    //         }

    //         function addLabel(beginText, correctLabel, absBeginIndex) {
    //             const suggestion = `${beginText}\n\\label{${correctLabel}}`;
    //             emitSuggestion.call(this,
    //                 beginText, suggestion, absBeginIndex,
    //                 `Add missing label \\label{${correctLabel}}`,
    //                 {
    //                     isLabelAdd: true,
    //                     newKey: correctLabel,
    //                 }
    //             );
    //         }


    //         function removeLabel(labelText, absLabelIndex) {
    //             emitSuggestion.call(this,
    //                 labelText, '',
    //                 absLabelIndex,
    //                 'Remove label from unnumbered equation'
    //             );
    //         }


    //         function extractBeginTag(fullMatch) {
    //             const m = /\\begin\s*\{[^}]+\*?\}/.exec(fullMatch);
    //             return m ? m : null;
    //         }
    //         const subRanges = [];
    //         const subScanPat = /\\begin\s*\{subequations\}([\s\S]*?)\\end\s*\{subequations\}/g;
    //         let sm;
    //         while ((sm = subScanPat.exec(texFile)) !== null) {
    //             if (isCommented(texFile, sm.index)) continue;
    //             const fullBlock = sm[0];
    //             const bodyText = sm[1];
    //             const bodyStart = sm.index + fullBlock.indexOf(bodyText);
    //             const firstNl = fullBlock.indexOf('\n');
    //             const beginLine = firstNl !== -1 ? fullBlock.slice(0, firstNl) : fullBlock;
    //             subRanges.push({
    //                 start: sm.index, end: sm.index + fullBlock.length,
    //                 bodyStart, bodyText, beginLine, beginLineAbs: sm.index,
    //             });
    //         }
    //         const standaloneEnvs = [];
    //         const eqScanPat = /\\begin\s*\{(equation|align|eqnarray|gather|multline)\s*(\*?)\}([\s\S]*?)\\end\s*\{\1\s*\2\}/g;
    //         let em;
    //         while ((em = eqScanPat.exec(texFile)) !== null) {
    //             if (isCommented(texFile, em.index)) continue;
    //             if (subRanges.some(r => em.index >= r.start && em.index < r.end)) continue;
    //             standaloneEnvs.push({
    //                 start: em.index, fullMatch: em[0],
    //                 envName: em[1], isUnnumbered: em[2] === '*', body: em[3],
    //             });
    //         }
    //         const orderedItems = [
    //             ...subRanges.map(r => ({ pos: r.start })),
    //             ...standaloneEnvs.filter(e => !e.isUnnumbered).map(e => ({ pos: e.start })),
    //         ].sort((a, b) => a.pos - b.pos);

    //         const counterMap = new Map();
    //         let counter = 1;
    //         for (const item of orderedItems) counterMap.set(item.pos, counter++);
    //         for (const sub of subRanges) {
    //             const groupNum = counterMap.get(sub.start);
    //             const groupLabel = `${filename}eqn${groupNum}`;
    //             const groupLineLabels = findAllLabels(sub.beginLine);
    //             if (groupLineLabels.length > 0) {
    //                 const gl = groupLineLabels[0];
    //                 fixLabel.call(this, gl.fullText, groupLabel, sub.beginLineAbs + gl.index);
    //             }
    //             const innerEnvPat = /\\begin\s*\{(equation|align|eqnarray|gather|multline)\s*(\*?)\}([\s\S]*?)\\end\s*\{\1\s*\2\}/g;
    //             let im;
    //             let subLetter = 0;

    //             while ((im = innerEnvPat.exec(sub.bodyText)) !== null) {
    //                 const innerFull = im[0];
    //                 const isUnnumbered = im[2] === '*';
    //                 const innerBody = im[3];
    //                 const innerAbsIdx = sub.bodyStart + im.index;
    //                 if (isCommented(texFile, innerAbsIdx)) continue;

    //                 const bodyOffsetInFull = innerFull.indexOf(innerBody);

    //                 if (isUnnumbered) {
    //                     for (const lbl of findAllLabels(innerBody))
    //                         removeLabel.call(this, lbl.fullText, innerAbsIdx + bodyOffsetInFull + lbl.index);
    //                     continue;
    //                 }
    //                 const rows = innerBody.split(/\\\\(?!\[)/);
    //                 const isMultiRow = rows.length > 1;

    //                 if (isMultiRow) {
    //                     let charPos = 0;
    //                     for (const row of rows) {
    //                         if (row.trim().length === 0) { charPos += row.length + 2; continue; }

    //                         const expectedLabel = `${groupLabel}${String.fromCharCode(97 + subLetter)}`;
    //                         const rowLabels = findAllLabels(row);

    //                         if (rowLabels.length > 0) {
    //                             for (const lbl of rowLabels)
    //                                 fixLabel.call(this, lbl.fullText, expectedLabel,
    //                                     innerAbsIdx + bodyOffsetInFull + charPos + lbl.index);
    //                         } else {
    //                             const beginTagMatch = extractBeginTag(innerFull);
    //                             if (beginTagMatch) {
    //                                 addLabel.call(this,
    //                                     beginTagMatch[0], expectedLabel,
    //                                     innerAbsIdx + beginTagMatch.index
    //                                 );
    //                             }
    //                         }

    //                         charPos += row.length + 2;
    //                         subLetter++;
    //                     }
    //                 } else {
    //                     const expectedLabel = `${groupLabel}${String.fromCharCode(97 + subLetter)}`;
    //                     const allLabelsInBody = findAllLabels(innerBody);

    //                     if (allLabelsInBody.length > 0) {
    //                         const lbl = allLabelsInBody[0];
    //                         fixLabel.call(this, lbl.fullText, expectedLabel,
    //                             innerAbsIdx + bodyOffsetInFull + lbl.index);
    //                     } else {
    //                         const beginTagMatch = extractBeginTag(innerFull);
    //                         if (beginTagMatch) {
    //                             addLabel.call(this,
    //                                 beginTagMatch[0], expectedLabel,
    //                                 innerAbsIdx + beginTagMatch.index
    //                             );
    //                         }
    //                     }

    //                     subLetter++;
    //                 }
    //             }
    //         }
    //         for (const env of standaloneEnvs) {
    //             const { start, fullMatch, isUnnumbered, body } = env;
    //             const bodyOffsetInFull = fullMatch.indexOf(body);
    //             const labels = findAllLabels(body);

    //             if (isUnnumbered) {

    //                 for (const lbl of labels)
    //                     removeLabel.call(this, lbl.fullText, start + bodyOffsetInFull + lbl.index);
    //             } else {
    //                 const expectedLabel = `${filename}eqn${counterMap.get(start)}`;

    //                 if (labels.length > 0) {

    //                     const lbl = labels[0];
    //                     fixLabel.call(this, lbl.fullText, expectedLabel,
    //                         start + bodyOffsetInFull + lbl.index);
    //                 } else {
    //                     const beginTagMatch = extractBeginTag(fullMatch);
    //                     if (beginTagMatch) {
    //                         addLabel.call(this,
    //                             beginTagMatch[0], expectedLabel,
    //                             start + beginTagMatch.index
    //                         );
    //                     }
    //                 }
    //             }
    //         }

    //         return staleIds;
    //     }
    // },


    BeginEquation: {
        name: "\\begin{equation}",
        description: "Fix/add/remove labels in all math environments",
        markerClass: "ace-Common-highlight",

        execute(
            texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []
        ) {
            const staleIds = new Set();
            const filename = (fileName || "doc").replace(/\.tex$/i, '');

            function isCommented(text, index) {
                let lineStart = index;
                while (lineStart > 0 && text[lineStart - 1] !== '\n') lineStart--;
                for (let i = lineStart; i < index; i++) {
                    if (text[i] === '%' && (i === 0 || text[i - 1] !== '\\')) return true;
                }
                return false;
            }

            function findAllLabels(text) {
                const results = [];
                const pat = /\\label\s*\{([^}]*?\S[^}]*?)\}/g;
                let m;
                while ((m = pat.exec(text)) !== null) {
                    if (!isCommented(text, m.index))
                        results.push({ fullText: m[0], inner: m[1], index: m.index });
                }
                return results;
            }

            function emitSuggestion(originalText, suggestionText, absIndex, description, extraStyleInfo = {}) {
                const position = getPositionFromIndex(texFile, absIndex, originalText.length);
                if (isRejectedByLivePosition(position, this.name, originalText)) return;
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) return;
                }
                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(
                        session, Range, position,
                        originalText, suggestionText,
                        this.markerClass, this.name,
                        description, anchorlist,
                        extraStyleInfo
                    );
                }
            }

            function fixLabel(labelText, correctLabel, absLabelIndex) {
                const expected = `\\label{${correctLabel}}`;
                if (labelText === expected) return;
                const m = /\\label\s*\{([^}]*)\}/.exec(labelText);
                if (!m) return;
                emitSuggestion.call(this,
                    labelText, expected, absLabelIndex,
                    `Fix label → \\label{${correctLabel}}`,
                    { isLabelFix: true, oldKey: m[1], newKey: correctLabel }
                );
            }

            function addLabelToBegin(beginText, correctLabel, absBeginIndex) {
                emitSuggestion.call(this,
                    beginText,
                    `${beginText}\n\\label{${correctLabel}}`,
                    absBeginIndex,
                    `Add missing label \\label{${correctLabel}}`,
                    { isLabelAdd: true, newKey: correctLabel }
                );
            }

            function addLabelToRowEnd(terminatorText, correctLabel, absTerminatorIndex) {
                emitSuggestion.call(this,
                    terminatorText,
                    `\\label{${correctLabel}} ${terminatorText}`,
                    absTerminatorIndex,
                    `Add missing label \\label{${correctLabel}}`,
                    { isLabelAddRow: true, newKey: correctLabel }
                );
            }

            function removeLabel(labelText, absLabelIndex) {
                emitSuggestion.call(this,
                    labelText, '', absLabelIndex,
                    'Remove label from unnumbered equation'
                );
            }


            const SINGLE_BLOCK_ENVS = new Set(['equation', 'multline', 'split']);
            const MULTI_ROW_ENVS = new Set(['align', 'alignat', 'gather', 'flalign', 'eqnarray']);

            function extractBeginTag(fullMatch, envName) {
                if (envName === 'alignat') {
                    const m = /\\begin\s*\{alignat\*?\}\s*\{[^}]*\}/.exec(fullMatch);
                    return m || null;
                }
                const m = /\\begin\s*\{[^}]+\}/.exec(fullMatch);
                return m || null;
            }
            function splitBodyIntoRows(body, bodyAbsStart, endToken, endTokenAbsIdx) {
                const rows = [];
                const pat = /\\\\(?!\[)/g;
                let prev = 0;
                let m;

                while ((m = pat.exec(body)) !== null) {
                    if (isCommented(body, m.index)) continue;
                    const rowText = body.slice(prev, m.index);
                    if (rowText.trim().length > 0) {
                        rows.push({
                            text: rowText,
                            absStart: bodyAbsStart + prev,
                            termText: '\\\\',
                            termAbsIdx: bodyAbsStart + m.index,
                        });
                    }
                    prev = m.index + 2;
                }

                const lastText = body.slice(prev);
                if (lastText.trim().length > 0) {
                    rows.push({
                        text: lastText,
                        absStart: bodyAbsStart + prev,
                        termText: endToken,
                        termAbsIdx: endTokenAbsIdx,
                        isLast: true,
                    });
                }

                return rows;
            }

            function processRows(rows, baseLabel, envInfo) {
                const multiRow = rows.length > 1;

                for (let i = 0; i < rows.length; i++) {
                    const row = rows[i];
                    const expectedLabel = multiRow
                        ? `${baseLabel}${String.fromCharCode(97 + i)}`
                        : baseLabel;

                    const rowLabels = findAllLabels(row.text);

                    if (rowLabels.length > 0) {
                        for (const lbl of rowLabels) {
                            fixLabel.call(this, lbl.fullText, expectedLabel, row.absStart + lbl.index);
                        }
                    } else {
                        if (!multiRow) {

                            const beginTag = extractBeginTag(envInfo.fullMatch, envInfo.envName);
                            if (beginTag) {
                                addLabelToBegin.call(this, beginTag[0], expectedLabel, envInfo.start + beginTag.index);
                            }
                        } else {
                            addLabelToRowEnd.call(this, row.termText, expectedLabel, row.termAbsIdx);
                        }
                    }
                }
            }

            const subRanges = [];
            {
                const pat = /\\begin\s*\{subequations\}([\s\S]*?)\\end\s*\{subequations\}/g;
                let m;
                while ((m = pat.exec(texFile)) !== null) {
                    if (isCommented(texFile, m.index)) continue;
                    const bodyStart = m.index + m[0].indexOf(m[1]);
                    const endToken = '\\end{subequations}';
                    const endAbsIdx = m.index + m[0].lastIndexOf(endToken);
                    subRanges.push({
                        start: m.index, end: m.index + m[0].length,
                        bodyStart, bodyText: m[1], endAbsIdx, endToken,
                    });
                }
            }
            const ENV_NAMES = 'equation|align|alignat|gather|multline|flalign|split|eqnarray';
            const outerEnvs = [];
            {
                const pat = new RegExp(
                    `\\\\begin\\s*\\{(${ENV_NAMES})(\\*?)\\}(?:\\s*\\{[^}]*\\})?` +
                    `([\\s\\S]*?)` +
                    `\\\\end\\s*\\{\\1\\2\\}`,
                    'g'
                );
                let m;
                while ((m = pat.exec(texFile)) !== null) {
                    if (isCommented(texFile, m.index)) continue;
                    if (subRanges.some(r => m.index > r.start && m.index < r.end)) continue;

                    const endToken = `\\end{${m[1]}${m[2]}}`;
                    const endAbsIdx = m.index + m[0].lastIndexOf(endToken);

                    outerEnvs.push({
                        start: m.index, end: m.index + m[0].length,
                        fullMatch: m[0], envName: m[1],
                        isUnnumbered: m[2] === '*',
                        body: m[3], endAbsIdx, endToken,
                    });
                }
            }

            const counterMap = new Map();
            let eqCounter = 1;
            for (const env of outerEnvs) {
                if (!env.isUnnumbered) counterMap.set(env.start, eqCounter++);
            }


            for (const env of outerEnvs) {
                const { start, end, fullMatch, envName, isUnnumbered, body, endAbsIdx, endToken } = env;
                const bodyOffsetInFull = fullMatch.indexOf(body);
                const bodyAbsStart = start + bodyOffsetInFull;
                if (isUnnumbered) {
                    for (const lbl of findAllLabels(body))
                        removeLabel.call(this, lbl.fullText, bodyAbsStart + lbl.index);
                    continue;
                }

                const baseLabel = `epl${filename}eqn${counterMap.get(start)}`;
                const envInfo = { start, fullMatch, envName };
                const sub = subRanges.find(r => r.start >= start && r.end <= end);

                if (sub) {

                    const outerLabels = findAllLabels(body).filter(lbl => {
                        const abs = bodyAbsStart + lbl.index;
                        return abs < sub.start || abs >= sub.end;
                    });

                    if (outerLabels.length > 0) {
                        fixLabel.call(this, outerLabels[0].fullText, baseLabel,
                            bodyAbsStart + outerLabels[0].index);
                    } else {
                        const beginTag = extractBeginTag(fullMatch, envName);
                        if (beginTag)
                            addLabelToBegin.call(this, beginTag[0], baseLabel, start + beginTag.index);
                    }

                    // Per-row labels inside subequations
                    const subRows = splitBodyIntoRows(
                        sub.bodyText, sub.bodyStart, sub.endToken, sub.endAbsIdx
                    );
                    processRows.call(this, subRows, baseLabel, envInfo);
                    continue;
                }
                if (SINGLE_BLOCK_ENVS.has(envName)) {
                    const labels = findAllLabels(body);
                    if (labels.length > 0) {
                        fixLabel.call(this, labels[0].fullText, baseLabel, bodyAbsStart + labels[0].index);
                    } else {
                        const beginTag = extractBeginTag(fullMatch, envName);
                        if (beginTag)
                            addLabelToBegin.call(this, beginTag[0], baseLabel, start + beginTag.index);
                    }
                    continue;
                }
                if (MULTI_ROW_ENVS.has(envName)) {
                    const rows = splitBodyIntoRows(body, bodyAbsStart, endToken, endAbsIdx);
                    processRows.call(this, rows, baseLabel, envInfo);
                    continue;
                }
            }

            return staleIds;
        }
    },



    BeginTable: {
        name: "\\begin{table}",
        description: "Fix wrong labels in tables and subtables, and add missing labels",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = [], rejectedSuggestions = []) {
            const staleIds = new Set();
            const filename = (fileName || "doc").replace(/\.tex$/i, '');

            // ── Helpers ──────────────────────────────────────────────────────────

            function isCommented(text, index) {
                let lineStart = index;
                while (lineStart > 0 && text[lineStart - 1] !== '\n') lineStart--;
                for (let i = lineStart; i < index; i++) {
                    if (text[i] === '%' && (i === 0 || text[i - 1] !== '\\')) return true;
                }
                return false;
            }

            function findAllLabels(text) {
                const results = [];
                const pat = /\\label\s*\{([^}]*?\S[^}]*?)\}/g;
                let m;
                while ((m = pat.exec(text)) !== null) {
                    if (!isCommented(text, m.index)) {
                        results.push({ fullText: m[0], inner: m[1], index: m.index });
                    }
                }
                return results;
            }

            function emitSuggestion(originalText, suggestionText, absIndex, description, extraStyleInfo = {}) {
                const position = getPositionFromIndex(texFile, absIndex, originalText.length);
                if (isRejectedByLivePosition(position, this.name, originalText)) return;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) return;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position,
                        originalText, suggestionText,
                        this.markerClass, this.name,
                        description, anchorlist,
                        extraStyleInfo);
                }
            }

            function fixLabel(labelText, correctLabel, absLabelIndex) {
                const expected = `\\label{${correctLabel}}`;
                if (labelText === expected) return;

                const oldKeyMatch = /\\label\s*\{([^}]*)\}/.exec(labelText);
                if (!oldKeyMatch) return;
                const oldKey = oldKeyMatch[1];
                const newKey = correctLabel;

                emitSuggestion.call(this,
                    labelText, expected, absLabelIndex,
                    `Fix table label → \\label{${newKey}}`,
                    { isLabelFix: true, oldKey, newKey } // Matches your action file logic
                );
            }

            function addLabel(beginText, correctLabel, absBeginIndex) {
                const suggestion = `${beginText}\n\\label{${correctLabel}}`;
                emitSuggestion.call(this,
                    beginText, suggestion, absBeginIndex,
                    `Add missing label \\label{${correctLabel}}`,
                    { isLabelAdd: true, newKey: correctLabel } // Matches your action file logic
                );
            }

            function extractBeginTag(fullMatch) {
                const m = /\\begin\s*\{table\*?\}/i.exec(fullMatch);
                if (m) return m;
                const sub = /\\begin\s*\{subtable\}/i.exec(fullMatch);
                return sub ? sub : null;
            }

            // ── 1. PRE-SCAN: Collect all Table blocks ───────────────────────────
            const tablePat = /\\begin\{table\*?\}([\s\S]*?)\\end\{table\*?\}/gi;
            const tables = [];
            let tm;
            while ((tm = tablePat.exec(texFile)) !== null) {
                if (isCommented(texFile, tm.index)) continue;
                tables.push({
                    start: tm.index,
                    fullMatch: tm[0],
                    body: tm[1],
                    bodyStart: tm.index + tm[0].indexOf(tm[1]),
                });
            }

            // ── 2. MAIN PASS: Process tables sequentially ───────────────────────
            let tableCounter = 1;

            for (const tab of tables) {
                const { fullMatch, body, bodyStart, start } = tab;
                const mainTabLabel = `epl${filename}tab${tableCounter}`;

                // Define Subtable pattern
                const subtabPat = /\\begin\{subtable\}(?:\[[^\]]*\])?\s*\{[^}]*\}([\s\S]*?)\\end\{subtable\}/gi;

                const subtabBoundaries = [];
                let stm;
                subtabPat.lastIndex = 0;
                while ((stm = subtabPat.exec(body)) !== null) {
                    subtabBoundaries.push({
                        start: stm.index,
                        end: stm.index + stm[0].length,
                        contentBody: stm[1],
                        fullMatch: stm[0]
                    });
                }

                // A. Handle Main Table Label
                const allLabelsInTable = findAllLabels(body);
                const mainLevelLabel = allLabelsInTable.find(lbl => {
                    return !subtabBoundaries.some(sub => lbl.index >= sub.start && lbl.index < sub.end);
                });

                if (mainLevelLabel) {
                    fixLabel.call(this, mainLevelLabel.fullText, mainTabLabel, bodyStart + mainLevelLabel.index);
                } else {
                    // If no label exists in the main table, add it after \begin{table}
                    const beginTagMatch = extractBeginTag(fullMatch);
                    if (beginTagMatch) {
                        addLabel.call(this, beginTagMatch[0], mainTabLabel, start + beginTagMatch.index);
                    }
                }

                // B. Handle Subtable Labels
                subtabBoundaries.forEach((sub, index) => {
                    const subLetter = String.fromCharCode(97 + index); // a, b, c...
                    const expectedSubLabel = `${mainTabLabel}${subLetter}`;
                    const innerLabels = findAllLabels(sub.contentBody);

                    if (innerLabels.length > 0) {
                        const lbl = innerLabels[0];
                        const subBodyOffset = sub.fullMatch.indexOf(sub.contentBody);
                        fixLabel.call(this,
                            lbl.fullText,
                            expectedSubLabel,
                            bodyStart + sub.start + subBodyOffset + lbl.index
                        );
                    } else {
                        // Add label to subtable if missing
                        const subBeginTag = /\\begin\s*\{subtable\}(?:\[[^\]]*\])?\s*\{[^}]*\}/i.exec(sub.fullMatch);
                        if (subBeginTag) {
                            addLabel.call(this, subBeginTag[0], expectedSubLabel, bodyStart + sub.start + subBeginTag.index);
                        }
                    }
                });

                tableCounter++;
            }

            return staleIds;
        }
    },

    // BeginFigure: {
    //     name: "\\begin{figure}",
    //     description: "Fix wrong labels in figures and subfigures, and update references",
    //     markerClass: "ace-Common-highlight",

    //     execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = [], rejectedSuggestions = []) {
    //         const staleIds = new Set();
    //         const filename = (fileName || "doc").replace(/\.tex$/i, '');

    //         // ── Helpers ──────────────────────────────────────────────────────────

    //         function isCommented(text, index) {
    //             let lineStart = index;
    //             while (lineStart > 0 && text[lineStart - 1] !== '\n') lineStart--;
    //             for (let i = lineStart; i < index; i++) {
    //                 if (text[i] === '%' && (i === 0 || text[i - 1] !== '\\')) return true;
    //             }
    //             return false;
    //         }

    //         /**
    //          * Strict label finder: ignores \label{} or \label{  }
    //          */
    //         function findAllLabels(text) {
    //             const results = [];
    //             const pat = /\\label\s*\{([^}]*?\S[^}]*?)\}/g;
    //             let m;
    //             while ((m = pat.exec(text)) !== null) {
    //                 if (!isCommented(text, m.index)) {
    //                     results.push({ fullText: m[0], inner: m[1], index: m.index });
    //                 }
    //             }
    //             return results;
    //         }

    //         function emitSuggestion(originalText, suggestionText, absIndex, description, extraStyleInfo = {}) {
    //             const styleName = extraStyleInfo._styleName || this.name;
    //             const position = getPositionFromIndex(texFile, absIndex, originalText.length);

    //             if (isRejectedByLivePosition(position, styleName, originalText)) return;
    //             const overlapping = getOverlappingAnchors(position, styleName, existingSuggestions);
    //             if (overlapping.length > 0) {
    //                 overlapping.forEach(old => staleIds.add(old.id));
    //             } else {
    //                 if (isAlreadyAnchored(position, styleName, existingSuggestions)) return;
    //             }

    //             if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
    //                 const { _styleName, ...cleanExtra } = extraStyleInfo;
    //                 createAnchor(session, Range, position,
    //                     originalText, suggestionText,
    //                     this.markerClass, styleName,
    //                     description, anchorlist,
    //                     cleanExtra);
    //             }
    //         }

    //         function fixLabel(labelText, correctLabel, absLabelIndex) {
    //             const expected = `\\label{${correctLabel}}`;
    //             if (labelText === expected) return;

    //             const oldKeyMatch = /\\label\s*\{([^}]*)\}/.exec(labelText);
    //             if (!oldKeyMatch) return;
    //             const oldKey = oldKeyMatch[1];
    //             const newKey = correctLabel;

    //             const groupStyleInfo = { isLabelRenameGroup: true, oldKey, newKey };

    //             emitSuggestion.call(this, labelText, expected, absLabelIndex, `Fix label → \\label{${newKey}}`, groupStyleInfo);

    //             const escaped = oldKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    //             const refPat = new RegExp(`(\\\\(?:eq|auto|page|[cC])?ref\\{[^}]*?)\\b${escaped}\\b([^}]*?\\})`, 'g');
    //             let rm;
    //             while ((rm = refPat.exec(texFile)) !== null) {
    //                 if (isCommented(texFile, rm.index)) continue;
    //                 const refFull = rm[0];
    //                 const refSuggestion = rm[1] + newKey + rm[2];
    //                 if (refFull === refSuggestion) continue;
    //                 emitSuggestion.call(this, refFull, refSuggestion, rm.index, `Fix reference → ${refSuggestion}`, groupStyleInfo);
    //             }
    //         }

    //         function fixIncludegraphics(fullInclude, newInclude, absIdx) {
    //             if (fullInclude === newInclude) return;
    //             emitSuggestion.call(this, fullInclude, newInclude, absIdx, `Rename \\includegraphics file`, { _styleName: this.name + '_include' });
    //         }

    //         // ── 1. PRE-SCAN: Collect Figure Blocks ──────────────────────────────
    //         const figurePat = /\\begin\{figure\*?\}([\s\S]*?)\\end\{figure\*?\}/gi;
    //         const figures = [];
    //         let fm;
    //         while ((fm = figurePat.exec(texFile)) !== null) {
    //             if (isCommented(texFile, fm.index)) continue;
    //             figures.push({
    //                 start: fm.index,
    //                 fullMatch: fm[0],
    //                 body: fm[1],
    //                 bodyStart: fm.index + fm[0].indexOf(fm[1]),
    //             });
    //         }

    //         // ── 2. MAIN PASS: Process figures ───────────────────────────────────
    //         let figCounter = 1;

    //         for (const fig of figures) {
    //             const { body, bodyStart } = fig;
    //             const figLabel = `${filename}fig${figCounter}`;

    //             // Define Subfigure pattern
    //             const subfigPat = /\\begin\{subfigure\}(?:\[[^\]]*\])?\s*\{[^}]*\}([\s\S]*?)\\end\{subfigure\}/gi;

    //             // Map subfigure locations
    //             const subfigBoundaries = [];
    //             let sm;
    //             subfigPat.lastIndex = 0;
    //             while ((sm = subfigPat.exec(body)) !== null) {
    //                 subfigBoundaries.push({
    //                     start: sm.index,
    //                     end: sm.index + sm[0].length,
    //                     contentBody: sm[1],
    //                     fullMatch: sm[0]
    //                 });
    //             }

    //             // A. Figure-level Label (Strict check)
    //             const allLabelsInFig = findAllLabels(body);
    //             const mainLevelLabel = allLabelsInFig.find(lbl => {
    //                 return !subfigBoundaries.some(sub => lbl.index >= sub.start && lbl.index < sub.end);
    //             });

    //             if (mainLevelLabel) {
    //                 fixLabel.call(this, mainLevelLabel.fullText, figLabel, bodyStart + mainLevelLabel.index);
    //             }

    //             // B. Subfigure Labels & Includegraphics
    //             subfigBoundaries.forEach((sub, index) => {
    //                 const subLetter = String.fromCharCode(97 + index);
    //                 const expectedSubLabel = `${figLabel}${subLetter}`;
    //                 const subBodyStart = bodyStart + sub.start + sub.fullMatch.indexOf(sub.contentBody);

    //                 // Sub-label
    //                 const innerLabels = findAllLabels(sub.contentBody);
    //                 if (innerLabels.length > 0) {
    //                     fixLabel.call(this, innerLabels[0].fullText, expectedSubLabel, subBodyStart + innerLabels[0].index);
    //                 }

    //                 // Sub-includegraphics
    //                 const incPat = /\\includegraphics\s*(?:\[[^\]]*\])?\s*\{([^}]*)\}/gi;
    //                 let incMatch;
    //                 while ((incMatch = incPat.exec(sub.contentBody)) !== null) {
    //                     if (isCommented(sub.contentBody, incMatch.index)) continue;
    //                     const currentFile = incMatch[1].trim();
    //                     const ext = currentFile.match(/\.[^.]+$/)?.[0] || '';
    //                     const expectedBase = `${filename}_f${figCounter}_sf${index + 1}_pr`;
    //                     const newInc = incMatch[0].replace(/(\{)[^}]*(\})/, `$1${expectedBase}${ext}$2`);
    //                     fixIncludegraphics.call(this, incMatch[0], newInc, subBodyStart + incMatch.index);
    //                 }
    //             });

    //             // C. Main Figure Includegraphics (outside subfigures)
    //             const incPat = /\\includegraphics\s*(?:\[[^\]]*\])?\s*\{([^}]*)\}/gi;
    //             let mInc;
    //             while ((mInc = incPat.exec(body)) !== null) {
    //                 if (isCommented(body, mInc.index)) continue;
    //                 // Check if this include is inside a subfigure
    //                 const isInsideSub = subfigBoundaries.some(sub => mInc.index >= sub.start && mInc.index < sub.end);
    //                 if (isInsideSub) continue;

    //                 const currentFile = mInc[1].trim();
    //                 const ext = currentFile.match(/\.[^.]+$/)?.[0] || '';
    //                 // const expectedBase = `${filename}_f${figCounter}_pr`;
    //                 // const newInc = mInc[0].replace(/(\{)[^}]*(\})/, `$1${expectedBase}${ext}$2`);
    //                 const expectedBase = `${filename}f${figCounter}_pr`;
    //                 const newInc = mInc[0].replace(/(\{)[^}]*(\})/, `$1${expectedBase}`);
    //                 fixIncludegraphics.call(this, mInc[0], newInc, bodyStart + mInc.index);
    //             }

    //             figCounter++;
    //         }

    //         return staleIds;
    //     }
    // },

    BeginFigure: {
        name: "\\begin{figure}",
        description: "Fix wrong labels in figures and subfigures, add missing labels, auto-update all refs on accept",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = [], rejectedSuggestions = []) {
            const staleIds = new Set();
            const filename = (fileName || "doc").replace(/\.tex$/i, '');

            // ── Helpers ────────────────────────────────────────────────────────────────

            function isCommented(text, index) {
                let lineStart = index;
                while (lineStart > 0 && text[lineStart - 1] !== '\n') lineStart--;
                for (let i = lineStart; i < index; i++) {
                    if (text[i] === '%' && (i === 0 || text[i - 1] !== '\\')) return true;
                }
                return false;
            }

            // Strict label finder: ignores \label{} or \label{ }
            function findAllLabels(text) {
                const results = [];
                const pat = /\\label\s*\{([^}]*?\S[^}]*?)\}/g;
                let m;
                while ((m = pat.exec(text)) !== null) {
                    if (!isCommented(text, m.index)) {
                        results.push({ fullText: m[0], inner: m[1], index: m.index });
                    }
                }
                return results;
            }

            // ── Core emitter ───────────────────────────────────────────────────────────

            function emitSuggestion(originalText, suggestionText, absIndex, description, extraStyleInfo = {}) {
                const styleName = extraStyleInfo._styleName || this.name;
                const position = getPositionFromIndex(texFile, absIndex, originalText.length);

                if (isRejectedByLivePosition(position, styleName, originalText)) return;
                const overlapping = getOverlappingAnchors(position, styleName, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, styleName, existingSuggestions)) return;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    const { _styleName, ...cleanExtra } = extraStyleInfo;
                    createAnchor(session, Range, position,
                        originalText, suggestionText,
                        this.markerClass, styleName,
                        description, anchorlist,
                        cleanExtra);
                }
            }

            // ── fixLabel ───────────────────────────────────────────────────────────────
            // Anchors ONLY the \label{oldKey} itself.
            // styleInfo carries isLabelFix + oldKey + newKey so applyAcceptToEditor
            // can sweep all refs after replacing the label.
            // ──────────────────────────────────────────────────────────────────────────
            function fixLabel(labelText, correctLabel, absLabelIndex) {
                const expected = `\\label{${correctLabel}}`;
                if (labelText === expected) return; // already correct — nothing to do

                const oldKeyMatch = /\\label\s*\{([^}]*)\}/.exec(labelText);
                if (!oldKeyMatch) return;
                const oldKey = oldKeyMatch[1];
                const newKey = correctLabel;

                // Anchor ONLY the \label — no ref/eqref anchors
                emitSuggestion.call(this,
                    labelText, expected, absLabelIndex,
                    `Fix label → \\label{${newKey}}`,
                    {
                        isLabelFix: true,  // flag for applyAcceptToEditor
                        oldKey,
                        newKey,
                    }
                );
            }

            // ── addLabel ───────────────────────────────────────────────────────────────
            // Fires when a figure/subfigure has NO \label at all.
            // Anchors the \begin{figure} or \begin{subfigure} tag and suggests
            // inserting \label{correctLabel} on the next line after it.
            // styleInfo carries isLabelAdd + newKey (no oldKey — no refs to sweep).
            // ──────────────────────────────────────────────────────────────────────────
            function addLabel(beginText, correctLabel, absBeginIndex) {
                const suggestion = `${beginText}\n\\label{${correctLabel}}`;
                emitSuggestion.call(this,
                    beginText, suggestion, absBeginIndex,
                    `Add missing label \\label{${correctLabel}}`,
                    {
                        isLabelAdd: true,  // flag for applyAcceptToEditor
                        newKey: correctLabel,
                    }
                );
            }

            // ── fixIncludegraphics ─────────────────────────────────────────────────────
            function fixIncludegraphics(fullInclude, newInclude, absIdx) {
                if (fullInclude === newInclude) return;
                emitSuggestion.call(this,
                    fullInclude, newInclude, absIdx,
                    `Rename \\includegraphics file`,
                    { _styleName: this.name + '_include' }
                );
            }

            // ── Helper: extract \begin tag from a full env match ──────────────────────
            function extractBeginTag(fullMatch) {
                return /\\begin\s*\{[^}]+\*?\}(?:\[[^\]]*\])?(?:\s*\{[^}]*\})?/.exec(fullMatch);
            }

            // ─────────────────────────────────────────────────────────────────────────
            // 1. PRE-SCAN: Collect Figure Blocks
            // ─────────────────────────────────────────────────────────────────────────
            const figurePat = /\\begin\{figure\*?\}([\s\S]*?)\\end\{figure\*?\}/gi;
            const figures = [];
            let fm;
            while ((fm = figurePat.exec(texFile)) !== null) {
                if (isCommented(texFile, fm.index)) continue;
                figures.push({
                    start: fm.index,
                    fullMatch: fm[0],
                    body: fm[1],
                    bodyStart: fm.index + fm[0].indexOf(fm[1]),
                });
            }

            // ─────────────────────────────────────────────────────────────────────────
            // 2. MAIN PASS: Process figures
            // ─────────────────────────────────────────────────────────────────────────
            let figCounter = 1;

            for (const fig of figures) {
                const { fullMatch, body, bodyStart } = fig;
                const figLabel = `epl${filename}fig${figCounter}`;

                // ── Define Subfigure pattern ──
                const subfigPat = /\\begin\{subfigure\}(?:\[[^\]]*\])?\s*\{[^}]*\}([\s\S]*?)\\end\{subfigure\}/gi;

                // ── Map subfigure locations ──
                const subfigBoundaries = [];
                let sm;
                subfigPat.lastIndex = 0;
                while ((sm = subfigPat.exec(body)) !== null) {
                    subfigBoundaries.push({
                        start: sm.index,
                        end: sm.index + sm[0].length,
                        contentBody: sm[1],
                        fullMatch: sm[0],
                    });
                }

                // ── A. Figure-level Label ──────────────────────────────────────────────
                const allLabelsInFig = findAllLabels(body);
                const mainLevelLabel = allLabelsInFig.find(lbl =>
                    !subfigBoundaries.some(sub => lbl.index >= sub.start && lbl.index < sub.end)
                );

                if (mainLevelLabel) {
                    // Label EXISTS → fix if wrong (anchor only the \label)
                    fixLabel.call(this, mainLevelLabel.fullText, figLabel, bodyStart + mainLevelLabel.index);
                } else {
                    // No label → anchor \begin{figure} and suggest adding label
                    const beginTagMatch = extractBeginTag(fullMatch);
                    if (beginTagMatch) {
                        addLabel.call(this,
                            beginTagMatch[0], figLabel,
                            fig.start + beginTagMatch.index
                        );
                    }
                }

                // ── B. Subfigure Labels & Includegraphics ──────────────────────────────
                subfigBoundaries.forEach((sub, index) => {
                    const subLetter = String.fromCharCode(97 + index);
                    const expectedSubLabel = `${figLabel}${subLetter}`;
                    const subBodyStart = bodyStart + sub.start + sub.fullMatch.indexOf(sub.contentBody);

                    // Sub-label
                    const innerLabels = findAllLabels(sub.contentBody);
                    if (innerLabels.length > 0) {
                        // Label EXISTS → fix if wrong (anchor only the \label)
                        fixLabel.call(this,
                            innerLabels[0].fullText, expectedSubLabel,
                            subBodyStart + innerLabels[0].index
                        );
                    } else {
                        // No label → anchor \begin{subfigure} and suggest adding label
                        const subBeginTagMatch = extractBeginTag(sub.fullMatch);
                        if (subBeginTagMatch) {
                            addLabel.call(this,
                                subBeginTagMatch[0], expectedSubLabel,
                                bodyStart + sub.start + subBeginTagMatch.index
                            );
                        }
                    }

                    // Sub-includegraphics
                    const incPat = /\\includegraphics\s*(?:\[[^\]]*\])?\s*\{([^}]*)\}/gi;
                    let incMatch;
                    while ((incMatch = incPat.exec(sub.contentBody)) !== null) {
                        if (isCommented(sub.contentBody, incMatch.index)) continue;
                        const currentFile = incMatch[1].trim();
                        const ext = currentFile.match(/\.[^.]+$/)?.[0] || '';
                        const expectedBase = `${filename}_f${figCounter}_sf${index + 1}_pr`;
                        const newInc = incMatch[0].replace(/(\{)[^}]*(\})/, `$1${expectedBase}${ext}$2`);
                        fixIncludegraphics.call(this, incMatch[0], newInc, subBodyStart + incMatch.index);
                    }
                });

                // ── C. Main Figure Includegraphics (outside subfigures) ───────────────
                const incPat = /\\includegraphics\s*(?:\[[^\]]*\])?\s*\{([^}]*)\}/gi;
                let mInc;
                while ((mInc = incPat.exec(body)) !== null) {
                    if (isCommented(body, mInc.index)) continue;

                    // Skip includes inside subfigures
                    const isInsideSub = subfigBoundaries.some(
                        sub => mInc.index >= sub.start && mInc.index < sub.end
                    );
                    if (isInsideSub) continue;

                    const currentFile = mInc[1].trim();
                    const ext = currentFile.match(/\.[^.]+$/)?.[0] || ''; // Extracts .eps, .pdf, etc.
                    const expectedBase = `${filename}f${figCounter}_pr`;

                    // FIX: Add ${ext} before the closing brace $2
                    const newInc = mInc[0].replace(/(\{)[^}]*(\})/, `$1${expectedBase}${ext}$2`);

                    fixIncludegraphics.call(this, mInc[0], newInc, bodyStart + mInc.index);
                }

                figCounter++;
            }

            return staleIds;
        }
    },

    // properLabelRef: {
    //     name: "Proper Label and Ref",
    //     description: "Rename labels to match filename:typeN format and update all refs",
    //     markerClass: "ace-Common-highlight",

    //     execute(texFile, session, Range, anchorlist,
    //         fileName, Allfiles,
    //         existingSuggestions = [],
    //         rejectedSuggestions = []) {
    //         const staleIds = new Set();
    //         const filename = (fileName || "doc").replace(/\.tex$/i, '');

    //         const envTypeMap = {
    //             'equation': 'eqn',
    //             'align': 'arrayeqn',
    //             'eqnarray': 'arrayeqn',
    //             'gather': 'arrayeqn',
    //             'multline': 'arrayeqn',
    //             'figure': 'fig',
    //             'figure*': 'fig',
    //             'table': 'tab',
    //             'table*': 'tab',
    //             'algorithm': 'alg',
    //             'listing': 'lst',
    //         };

    //         function isCommented(text, index) {
    //             let lineStart = index;
    //             while (lineStart > 0 && text[lineStart - 1] !== '\n') lineStart--;
    //             for (let i = lineStart; i < index; i++) {
    //                 if (text[i] === '%' && (i === 0 || text[i - 1] !== '\\')) return true;
    //             }
    //             return false;
    //         }

    //         const envPattern = /\\begin\s*\{(equation|align|eqnarray|gather|multline|figure\*?|table\*?|algorithm|listing)\s*(\*?)\}([\s\S]*?)\\end\s*\{\1\s*\2\}/g;
    //         const typeCounters = {};
    //         const collected = [];
    //         let match;

    //         while ((match = envPattern.exec(texFile)) !== null) {
    //             const fullMatch = match[0];
    //             const envName = match[1];
    //             const isUnnumbered = match[2] === '*' || envName.endsWith('*');
    //             const envBody = match[3];
    //             const matchIndex = match.index;

    //             if (isCommented(texFile, matchIndex)) continue;
    //             if (isUnnumbered) continue;

    //             const typePrefix = envTypeMap[envName];
    //             if (!typePrefix) continue;

    //             if (!typeCounters[typePrefix]) typeCounters[typePrefix] = 0;
    //             typeCounters[typePrefix]++;
    //             const expectedLabel = `${filename}${typePrefix}${typeCounters[typePrefix]}`;

    //             const labelPattern = /\\label\{([^}]*)\}/gi;
    //             let labelMatch = null, tempMatch;
    //             while ((tempMatch = labelPattern.exec(envBody)) !== null) {
    //                 if (!isCommented(envBody, tempMatch.index)) { labelMatch = tempMatch; break; }
    //             }

    //             collected.push({ fullMatch, envName, envBody, matchIndex, expectedLabel, labelMatch });
    //         }

    //         for (const env of collected) {
    //             const { fullMatch, envName, matchIndex, expectedLabel, labelMatch } = env;

    //             if (labelMatch) {
    //                 const currentLabelName = labelMatch[1];
    //                 if (currentLabelName === expectedLabel) continue;

    //                 const labelText = labelMatch[0];
    //                 const labelIndex = matchIndex + fullMatch.indexOf(labelText);
    //                 const position = getPositionFromIndex(texFile, labelIndex, labelText.length);

    //                 if (isRejectedByLivePosition(position, this.name, labelText)) continue;

    //                 const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
    //                 if (overlapping.length > 0) {
    //                     overlapping.forEach(old => staleIds.add(old.id));
    //                 } else {
    //                     if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
    //                 }

    //                 createAnchor(session, Range, position,
    //                     labelText, `\\label{${expectedLabel}}`,
    //                     this.markerClass, this.name,
    //                     `Rename \\label{${currentLabelName}} → \\label{${expectedLabel}}`,
    //                     anchorlist,
    //                     { oldLabelName: currentLabelName, newLabelName: expectedLabel }
    //                 );

    //             } else {
    //                 const firstNewline = fullMatch.indexOf('\n');
    //                 const firstLine = firstNewline !== -1 ? fullMatch.slice(0, firstNewline) : fullMatch;
    //                 const position = getPositionFromIndex(texFile, matchIndex, firstLine.length);
    //                 const suggestion = `${firstLine}\n\\label{${expectedLabel}}`;

    //                 if (isRejectedByLivePosition(position, this.name, firstLine)) continue;

    //                 const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
    //                 if (overlapping.length > 0) {
    //                     overlapping.forEach(old => staleIds.add(old.id));
    //                 } else {
    //                     if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
    //                 }

    //                 if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
    //                     createAnchor(session, Range, position,
    //                         firstLine, suggestion,
    //                         this.markerClass, this.name,
    //                         `Add \\label{${expectedLabel}} to \\begin{${envName}}`,
    //                         anchorlist,
    //                         { oldLabelName: null, newLabelName: expectedLabel }
    //                     );
    //                 }
    //             }
    //         }

    //         return staleIds;
    //     }
    // },
    FormatExample: {
        name: "Format e.g, i.e and et al",
        description: "Formats e.g, i.e to italics and et al with \\etal",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/FormatExampleBefore.png",
        afterImage: "/images/description/FormatExampleAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const mappings = [
                { pattern: /\be\.g\./g, suggestion: '\\textit{e.g.}' },
                { pattern: /\be\.g\b/g, suggestion: '\\textit{e.g.}' },
                { pattern: /\bi\.e\./g, suggestion: '\\textit{i.e.}' },
                { pattern: /\bi\.e\b/g, suggestion: '\\textit{i.e.}' },
                { pattern: /\bet al\./g, suggestion: '{\\etal}' },
                { pattern: /\bet al\b/g, suggestion: '{\\etal}' },
            ];

            const alreadyWrapped = (texFile, matchIndex, fullMatch) => {
                const before = texFile.substring(Math.max(0, matchIndex - 9), matchIndex);
                if (before.endsWith('\\textit{')) return true;

                const surroundCheck = texFile.substring(Math.max(0, matchIndex - 7), matchIndex + fullMatch.length + 2);
                if (surroundCheck.includes('\\etal')) return true;

                return false;
            };

            for (const { pattern, suggestion } of mappings) {
                pattern.lastIndex = 0;
                let match;
                while ((match = pattern.exec(texFile)) !== null) {
                    const fullMatch = match[0];
                    const matchIndex = match.index;

                    if (alreadyWrapped(texFile, matchIndex, fullMatch)) continue;
                    if (fullMatch.includes('\n')) continue;

                    const position = getPositionFromIndex(texFile, matchIndex, fullMatch.length);

                    // ── NEW LOGIC: Stale & Overlapping Check ──
                    if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                    const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                    if (overlapping.length > 0) {
                        // Mark old versions of this suggestion as stale so they are refreshed
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        // Check if this specific suggestion already exists to avoid duplicates
                        if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                    }

                    if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                        createAnchor(
                            session, Range, position,
                            fullMatch, suggestion,
                            this.markerClass, this.name,
                            this.description, anchorlist
                        );
                    }
                }
            }
            return staleIds; // Essential for the proper method
        }
    },
    CitationStyling: {
        name: "Citation Styling",
        description: "Updates citation references to match bibitem labels",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {
            const staleIds = new Set();
            const filename = (fileName || "doc").replace(/\.tex$/i, '');

            const bibliographyPattern = /\\begin\{thebibliography\}([\s\S]*?)\\end\{thebibliography\}/;
            const bibMatch = bibliographyPattern.exec(texFile);
            if (!bibMatch || !/\\bib[a-z]{0,5}em\{/.test(bibMatch[0])) return staleIds;

            const bibSection = bibMatch[0];
            const bibSectionIndex = bibMatch.index;
            let bibCounter = 1;
            const labelMap = {};

            const currentFileObj = Array.isArray(Allfiles?.data)
                ? Allfiles.data.find(f => f.name === fileName)
                : null;
            const currentFileUuid = currentFileObj?.file_uuid || null;

            // ── Pass 1: \bibitem anchors ──
            const bibitemPattern = /\\bib[a-z]{0,5}em\{([^}]*)\}/g;
            let match;

            while ((match = bibitemPattern.exec(bibSection)) !== null) {
                const fullMatch = match[0];
                const oldKey = match[1];
                const newKey = `${filename}bib${bibCounter}`;
                const matchIndex = bibSectionIndex + match.index;

                labelMap[oldKey] = newKey;

                const isExactBibitem = fullMatch.startsWith('\\bibitem{');
                if (isExactBibitem && oldKey !== newKey) {
                    const suggestion = `\\bibitem{${newKey}}`;
                    const position = getPositionFromIndex(texFile, matchIndex, fullMatch.length);

                    if (isRejectedByLivePosition(position, this.name, fullMatch)) {
                        bibCounter++; continue;
                    }

                    const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                    if (overlapping.length > 0) {
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        if (isAlreadyAnchored(position, this.name, existingSuggestions)) {
                            bibCounter++; continue;
                        }
                    }

                    if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                        createAnchor(session, Range, position,
                            fullMatch, suggestion,
                            this.markerClass, this.name,
                            `Update bibitem label to ${newKey}`,
                            anchorlist,
                            {
                                isBibitemKeyChange: true,
                                oldKey,
                                newKey,
                                allfiles: Allfiles,
                                currentFileUuid,
                            }
                        );
                    }
                }
                bibCounter++;
            }

            // ── Pass 2: \cite anchors ──
            const citePattern = /(\s*)~?\\cite\{([^}]*)\}/g;
            citePattern.lastIndex = 0;

            while ((match = citePattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const citeLabels = match[2];
                const matchIndex = match.index;

                const refs = citeLabels.split(',').map(r => r.trim().replace(/\n/g, ''));
                const newRefs = refs.map(r => labelMap[r] || r);
                if (!refs.some((r, i) => newRefs[i] !== r)) continue;

                const suggestion = `~\\cite{${newRefs.join(',')}}`;
                const position = getPositionFromIndex(texFile, matchIndex, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position,
                        fullMatch, suggestion,
                        this.markerClass, this.name,
                        'Update citation reference and ensure proper spacing',
                        anchorlist);
                }
            }

            return staleIds;
        }
    },
    BibitemFormatConvert: {
        name: "Bibitem Format Convert",
        description: "Converts \\bibitem entries from doc-style to EPL-style",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {
            const staleIds = new Set();

            const bibliographyPattern = /\\begin\{thebibliography\}([\s\S]*?)\\end\{thebibliography\}/;
            const bibMatch = bibliographyPattern.exec(texFile);
            if (!bibMatch || !bibMatch[0].includes('\\bibitem')) return staleIds;

            const bibSection = bibMatch[0];
            const bibSectionIndex = bibMatch.index;

            // ── Helper: Converts "J. Smith" to "Smith J." ──
            function convertSingleAuthor(raw) {
                raw = raw.trim();
                const normalised = raw.replace(/~/g, ' ');
                const tokens = normalised.split(/\s+/).filter(Boolean);
                const initials = [];
                let lastName = null;

                for (const token of tokens) {
                    // Identify initials (e.g., "A." or "J.-P.")
                    if (/^[A-Z][\w-]*\.$/.test(token)) {
                        initials.push(token);
                    } else {
                        lastName = token; // Assume the last non-period token is the surname
                    }
                }

                if (!lastName) return raw;
                const initialsStr = initials.join('~');
                return initialsStr ? `${lastName} ${initialsStr}` : lastName;
            }

            // ── Helper: Handles "Author A, Author B and Author C" ──
            function buildNameField(scContent) {
                // Standardize separators: change "and" to comma
                let content = scContent.replace(/\band\b/g, ',');
                const authors = content.split(',').map(p => p.trim()).filter(Boolean);

                const converted = authors.map(convertSingleAuthor);
                if (converted.length === 0) return null;
                if (converted.length === 1) return converted[0];

                // EPL Style uses \and for the final joiner
                return converted.slice(0, -1).join(', ') + ' \\and ' + converted[converted.length - 1];
            }

            // ── Helper: Extracts Journal, Vol, Year, Page ──
            function buildReviewField(rest) {
                if (!rest) return null;
                rest = rest.trim().replace(/\.\s*$/, '');

                // Regex Update: Added (?:pp?\.?\s*~?)? to make "p." or "pp." optional
                const m = rest.match(/^(.*?),\s*(\d+)\s*\((\d{4})\),?\s*(?:pp?\.?\s*~?)?(\d+)/);
                if (!m) return null;

                return `\\REVIEW{${m[1].trim()}}{${m[2]}}{${m[3]}}{${m[4]}}`;
            }

            const rawBlocks = bibSection
                .split(/(?=\\bib[a-z]{0,5}em\{)|(?=\\end\{thebibliography\})/)
                .filter(s => s.trim());

            let runningOffset = 0;

            for (const block of rawBlocks) {
                if (!/^\\bib[a-z]{0,5}em\{/.test(block)) {
                    runningOffset += block.length; continue;
                }

                const isExactBibitem = block.startsWith('\\bibitem{');
                const keyMatch = block.match(/^\\bib[a-z]{0,5}em\{([^}]*)\}([\s\S]*)/);
                if (!keyMatch || !isExactBibitem) { runningOffset += block.length; continue; }

                const bibKey = keyMatch[1];
                const rawBody = keyMatch[2].replace(/\s+$/, '');
                const flatBody = rawBody.replace(/\s+/g, ' ').trim();
                const absoluteIndex = bibSectionIndex + runningOffset;

                // Extract the Small Caps (Authors) and Emphasis (Journal) parts
                const scMatch = flatBody.match(/\{\\sc\s+([\s\S]*?)\}/);
                const emMatch = flatBody.match(/\{\\em\s+([\s\S]*?)\}\s*,?\s*([\s\S]*)/);

                if (!scMatch && !emMatch) { runningOffset += block.length; continue; }

                let journalRest = emMatch
                    ? `${emMatch[1]}, ${emMatch[2]}`.trim()
                    : flatBody.replace(/\{\\sc\s+[\s\S]*?\}/, '').replace(/^\s*,?\s*/, '').trim();

                const nameField = scMatch ? buildNameField(scMatch[1].trim()) : null;
                const reviewField = buildReviewField(journalRest);

                if (!nameField && !reviewField) { runningOffset += block.length; continue; }

                // Construct the EPL suggestion
                let suggestion = `\\bibitem{${bibKey}}\n`;
                if (nameField && reviewField) {
                    suggestion += `\\Name{${nameField}},\n${reviewField}.`;
                } else if (nameField) {
                    suggestion += `\\Name{${nameField}},`;
                } else {
                    suggestion += `${reviewField}.`;
                }

                const originalFull = `\\bibitem{${bibKey}}${rawBody}`;
                const position = getPositionFromIndex(texFile, absoluteIndex, originalFull.length);

                // ── Standard Validation Guards ──
                if (isRejectedByLivePosition(position, this.name, originalFull)) {
                    runningOffset += block.length; continue;
                }

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else if (isAlreadyAnchored(position, this.name, existingSuggestions)) {
                    runningOffset += block.length; continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position,
                        originalFull, suggestion,
                        this.markerClass, this.name,
                        'Convert bibitem to EPL \\Name + \\REVIEW format',
                        anchorlist);
                }

                runningOffset += block.length;
            }

            return staleIds;
        }
    },

    abstractUnifiedStyling: {
        name: "Standardize Abstract to Command",
        description: "Converts abstract environment to command and ensures ~ formatting while preserving line breaks.",
        markerClass: "ace-Common-highlight",
        execute: function (texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = []) {
            const staleIds = new Set();
            if (!texFile) return staleIds;

            // 1. Handle the Environment: \begin{abstract} ... \end{abstract}
            const envPattern = /\\begin\{abstract\}([\s\S]*?)\\end\{abstract\}/g;
            let envMatch;
            while ((envMatch = envPattern.exec(texFile)) !== null) {
                const fullMatch = envMatch[0];
                const innerContent = envMatch[1];

                // Clean text: remove leading ~ and trim edges, but KEEP internal \n
                const cleanText = innerContent.replace(/^~/, '').trim();
                const suggestion = `\\abstract{~${cleanText}}`;

                this.applyLogic(fullMatch, suggestion, envMatch.index, texFile, session, Range, anchorlist, existingSuggestions, staleIds);
            }

            // 2. Handle the existing Command: \abstract{...}
            const cmdPattern = /\\abstract\s*/g;
            let cmdMatch;
            while ((cmdMatch = cmdPattern.exec(texFile)) !== null) {
                const context = texFile.slice(Math.max(0, cmdMatch.index - 10), cmdMatch.index);
                if (context.includes("begin")) continue;

                const capturedBracedData = texFile.slice(cmdMatch.index + cmdMatch[0].length);
                const capturedText = getInsideTextofBraces(capturedBracedData);
                if (!capturedText) continue;

                const innerContent = capturedText.slice(1, -1);

                // Clean text: remove leading ~ and trim edges, but KEEP internal \n
                const cleanText = innerContent.replace(/^~/, '').trim();
                const formattedInner = `~${cleanText}`;

                // Only trigger if it doesn't start with ~ or has messy edges
                if (innerContent !== formattedInner) {
                    const fullMatch = `\\abstract${capturedText}`;
                    const suggestion = `\\abstract{${formattedInner}}`;
                    this.applyLogic(fullMatch, suggestion, cmdMatch.index, texFile, session, Range, anchorlist, existingSuggestions, staleIds);
                }
            }
            return staleIds;
        },

        applyLogic: function (fullMatch, suggestion, index, texFile, session, Range, anchorlist, existingSuggestions, staleIds) {
            const position = getPositionFromIndex(texFile, index, fullMatch.length);
            const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);

            if (overlapping.length > 0) {
                overlapping.forEach(old => staleIds.add(old.id));
            } else {
                if (isAlreadyAnchored(position, this.name, existingSuggestions, fullMatch)) return;
                if (isRejectedByLivePosition(position, this.name, fullMatch)) return;
            }

            if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                createAnchor(session, Range, position, fullMatch, suggestion, this.markerClass, this.name, this.description, anchorlist);
            }
        }
    },
    insertBeginDocument: {
        name: "Insert \\begin{document}",
        description: "Ensures \\maketitle appears right after \\begin{document}.",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/BeginDocumentBefore.png",
        afterImage: "/images/description/BeginDocumentAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {
            const staleIds = new Set();

            const beginDocPattern = /\\begin\{document\}/g;
            const maketitlePattern = /\\maketitle/g;

            const beginDocMatch = beginDocPattern.exec(texFile);
            const maketitleMatch = maketitlePattern.exec(texFile);

            // If no \begin{document}, we can't reliably place \maketitle
            if (!beginDocMatch) return staleIds;

            const beginDocIndex = beginDocMatch.index;
            const beginDocText = beginDocMatch[0];
            const afterBeginDoc = texFile.slice(beginDocIndex + beginDocText.length);

            // Check if \maketitle is already correctly placed (first non-whitespace)
            const isCorrectlyPlaced = /^\s*\\maketitle/.test(afterBeginDoc);

            // If it's already correct, do nothing
            if (isCorrectlyPlaced) return staleIds;

            // If we get here, \maketitle is either missing or misplaced somewhere else
            const pos = getPositionFromIndex(texFile, beginDocIndex, beginDocText.length);
            const position = {
                startRow: pos.startRow,
                startCol: pos.startCol,
                endRow: pos.endRow,
                endCol: pos.endCol,
            };

            // CRITICAL: Check if we already have an anchor at this \begin{document}
            if (isAlreadyAnchored(position, this.name, existingSuggestions)) return staleIds;
            if (isRejectedByLivePosition(position, this.name, beginDocText)) return staleIds;

            const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
            if (overlapping.length > 0) {
                overlapping.forEach(old => staleIds.add(old.id));
            }

            // Create the anchor with the styleInfo metadata
            createAnchor(
                session, Range, position,
                beginDocText,
                `\\begin{document}\n\\maketitle`, // Suggestion
                this.markerClass, this.name, this.description,
                anchorlist,
                {
                    name: this.name, // Fixes the "undefined" style name issue
                    maketitleAction: 'moveAndClean',
                    oldMaketitleIndex: maketitleMatch ? maketitleMatch.index : null
                }
            );

            return staleIds;
        }
    },
    AdjustMathBrackets: {
        name: "Adjust Math Brackets",
        description: "Add \\left and \\right to brackets enclosing math constructs inside equation environments only",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/AdjustBracketBefore.png",
        afterImage: "/images/description/AdjustBracketAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const equationPattern = /\\begin\{equation\}([\s\S]*?)\\end\{equation\}/g;
            const targets = "\\\\(frac|sum|int|prod)";
            const rules = [
                { pattern: new RegExp(`(?<!\\\\left)\\(([^()]*${targets}[\\s\\S]*?)\\)`, "g"), wrap: (inner) => `\\left(${inner}\\right)` },
                { pattern: new RegExp(`(?<!\\\\left)\\\\\\{([^{}]*${targets}[\\s\\S]*?)\\\\\\}`, "g"), wrap: (inner) => `\\left\\{${inner}\\right\\}` },
                { pattern: new RegExp(`(?<!\\\\left)\\[([^\\[\\]]*${targets}[\\s\\S]*?)\\]`, "g"), wrap: (inner) => `\\left[${inner}\\right]` },
                { pattern: new RegExp(`(?<!\\\\left)\\|([^|]*${targets}[\\s\\S]*?)\\|`, "g"), wrap: (inner) => `\\left|${inner}\\right|` },
            ];

            let eqMatch;
            while ((eqMatch = equationPattern.exec(texFile)) !== null) {
                const equationBody = eqMatch[1];
                const equationStartIndex = eqMatch.index + eqMatch[0].indexOf(equationBody);

                for (const { pattern, wrap } of rules) {
                    pattern.lastIndex = 0;
                    let match;
                    while ((match = pattern.exec(equationBody)) !== null) {
                        const fullMatch = match[0];
                        const suggestion = wrap(match[1]);
                        const absoluteIndex = equationStartIndex + match.index;
                        const position = getPositionFromIndex(texFile, absoluteIndex, fullMatch.length);

                        if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                        const overlapping = getOverlappingAnchors(position, this.name,
                            existingSuggestions);
                        if (overlapping.length > 0) {
                            overlapping.forEach(old => staleIds.add(old.id));
                        } else {
                            if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                        }

                        if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                            createAnchor(session, Range, position, fullMatch, suggestion,
                                this.markerClass, this.name, this.description, anchorlist);
                        }
                    }
                }
            }

            return staleIds;
        }
    },

    // figureRefStandardization: {
    //     name: "Figure Reference Formatting",
    //     description: "Standardize (Figure \\ref...) to (fig. \\ref...) with correct sub-ref parentheses (a)--(c)",
    //     markerClass: "ace-Common-highlight",

    //     execute: function (texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = []) {
    //         const staleIds = new Set();
    //         if (!texFile) return staleIds;

    //         // 1. Pattern to find starting (Figure \ref{...} or (Fig. \ref{...}
    //         const figStartPattern = /\((?:Figure|Fig\.?)\s*~?\\ref\{([^}]+)\}/gi;

    //         let m;
    //         while ((m = figStartPattern.exec(texFile)) !== null) {
    //             const startIndex = m.index;
    //             const label = m[1];

    //             // 2. USE BALANCED PARENTHESES LOGIC to find the full (Fig. \ref{...} (a)...) block
    //             let count = 0;
    //             let i = startIndex;
    //             let fullMatch = "";

    //             while (i < texFile.length) {
    //                 let char = texFile[i];
    //                 fullMatch += char;
    //                 if (char === "(") count++;
    //                 else if (char === ")") count--;

    //                 // Break once the opening bracket of the match is closed
    //                 if (count === 0 && fullMatch.includes("(")) break;
    //                 i++;
    //             }

    //             const refCommand = m[0]; // This is the "(Fig. \ref{label}" part
    //             const rawSuffix = fullMatch.slice(refCommand.length, -1); // The part between the ref and the final closing )

    //             // 3. STABILITY CHECK: Detect if a range already exists
    //             // This prevents (a)--(c) from being misinterpreted as just 'a' and 'c'
    //             const hasRange = /--|–|-/.test(rawSuffix);

    //             // 4. CLEANING & FORMATTING
    //             const letters = rawSuffix
    //                 .replace(/\band\b/gi, '')
    //                 .replace(/[^a-zA-Z]/g, '')
    //                 .split('')
    //                 .filter(Boolean);

    //             let formattedSubRef = "";

    //             if (letters.length === 0) {
    //                 // No sub-letters found
    //                 formattedSubRef = "";
    //             } else if (letters.length === 1) {
    //                 // Single sub-ref: (a)
    //                 formattedSubRef = `(${letters[0].toLowerCase()})`;
    //             } else if (hasRange || letters.length > 2) {
    //                 // If a dash was already there OR we have 3+ letters: (a)--(c)
    //                 const start = letters[0].toLowerCase();
    //                 const end = letters[letters.length - 1].toLowerCase();
    //                 formattedSubRef = `(${start})--(${end})`;
    //             } else if (letters.length === 2) {
    //                 // Exactly two letters and NO dash was present: (a)(b)
    //                 formattedSubRef = `(${letters[0].toLowerCase()})(${letters[1].toLowerCase()})`;
    //             }

    //             const suggestion = `(fig. ~\\ref{${label}}${formattedSubRef})`;

    //             // 5. STRICTOR GATEKEEPER
    //             // If the current text matches our suggestion perfectly, do nothing
    //             if (fullMatch.trim() === suggestion.trim()) {
    //                 continue;
    //             }

    //             // 6. POSITIONING & ANCHORING
    //             const position = getPositionFromIndex(texFile, startIndex, fullMatch.length);

    //             const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
    //             if (overlapping.length > 0) {
    //                 overlapping.forEach(old => staleIds.add(old.id));
    //             } else {
    //                 if (isAlreadyAnchored(position, this.name, existingSuggestions, fullMatch)) continue;
    //                 if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;
    //             }

    //             if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
    //                 createAnchor(
    //                     session, Range, position,
    //                     fullMatch, suggestion,
    //                     this.markerClass, this.name,
    //                     this.description, anchorlist
    //                 );
    //             }
    //         }
    //         return staleIds;
    //     }
    // },
    figureRefStandardization: {
        name: "Figure Reference Formatting",
        description: "Standardize (Figure \\ref...) to (fig. \\ref...) with correct sub-ref parentheses (a)--(c)",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles, existingSuggestions = [], rejectedSuggestions = []) {
            const staleIds = new Set();

            // 1. Initialize context. 
            // We use wholeDocument: true and keep excludes default (excluding comments/math)
            const context = buildStyleContext({
                texFile, session, Range, anchorlist, existingSuggestions, staleIds,
                styleName: this.name,
                markerClass: this.markerClass,
                scope: { wholeDocument: true }
            });

            // 2. Pattern to find the start: (Figure \ref{...} or (Fig. \ref{...}
            const figStartPattern = /\(\s*(?:Figure|Fig\.?)\s*~?\\ref\{([^}]+)\}/gi;

            context.segments.forEach(seg => {
                let m;
                // Reset regex for each segment
                figStartPattern.lastIndex = 0;

                while ((m = figStartPattern.exec(seg.text)) !== null) {
                    const startIndex = m.index;
                    const label = m[1];

                    // 3. Balanced parentheses logic to find the closing ')' of the citation
                    let count = 0;
                    let i = startIndex;
                    let fullMatch = "";

                    while (i < seg.text.length) {
                        let char = seg.text[i];
                        fullMatch += char;
                        if (char === "(") count++;
                        else if (char === ")") count--;

                        // Break once the outer parenthesis is closed
                        if (count === 0 && fullMatch.includes("(")) break;
                        i++;
                    }

                    const refCommand = m[0]; // e.g., "(Fig. \ref{label}"
                    // Extract part between the \ref{...} and the final )
                    const rawSuffix = fullMatch.slice(refCommand.length, -1);

                    // 4. Cleaning & Formatting Logic
                    const hasRange = /--|–|-/.test(rawSuffix);
                    const letters = rawSuffix
                        .replace(/\band\b/gi, '')
                        .replace(/[^a-zA-Z]/g, '')
                        .split('')
                        .filter(Boolean);

                    let formattedSubRef = "";

                    if (letters.length === 1) {
                        formattedSubRef = `(${letters[0].toLowerCase()})`;
                    } else if (hasRange || letters.length > 2) {
                        const start = letters[0].toLowerCase();
                        const end = letters[letters.length - 1].toLowerCase();
                        formattedSubRef = `(${start})--(${end})`;
                    } else if (letters.length === 2) {
                        formattedSubRef = `(${letters[0].toLowerCase()})(${letters[1].toLowerCase()})`;
                    }

                    const suggestion = `(fig. ~\\ref{${label}}${formattedSubRef})`;

                    // 5. Emit if a change is actually needed
                    if (fullMatch.trim() !== suggestion.trim()) {
                        // We use the segment's emit helper. 
                        // startIndex is the LOCAL offset inside this segment.
                        seg.emit(
                            fullMatch,
                            suggestion,
                            startIndex,
                            this.description
                        );
                    }
                }
            });

            return staleIds;
        }
    },
    equationRefStandardization: {
        name: "Equation Reference Formatting",
        description: "Lowercase equation citations inside parentheses: Eq. → eq., Equation → eq. (EPL style)",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles,
            existingSuggestions = [], rejectedSuggestions = []) {

            const staleIds = new Set();

            const { segments } = buildStyleContext({
                texFile, session, Range, anchorlist,
                existingSuggestions, staleIds,
                styleName: this.name,
                markerClass: this.markerClass,
                scope: { wholeDocument: true },
                excludeMath: true,
                overlapMode: 'simple',
            });

            const EQ_KEYS = ['Equations', 'Equation', 'Eqs.', 'Eqs', 'Eq.', 'Eq'];
            const EQ_MAP = Object.fromEntries(EQ_KEYS.map(k => [k, _CITATION_KW_MAP[k]]));
            const EQ_PAT = _buildCitationPattern(EQ_KEYS);

            const triggerPat = /\(/g;

            for (const seg of segments) {
                triggerPat.lastIndex = 0;
                let m;
                while ((m = triggerPat.exec(seg.text)) !== null) {
                    const paren = _extractBalancedParen(seg.text, m.index);
                    if (!paren.endsWith(')')) continue;

                    if (!/\b(?:Equation|Equations|Eq\.?s?)\b/.test(paren)) continue;

                    const suggestion = _applyCitationKeywords(paren, EQ_PAT, EQ_MAP);
                    if (paren === suggestion) continue;

                    seg.emit(paren, suggestion, m.index, this.description);
                }
            }

            return staleIds;
        },
    },
    tableRefStandardization: {
        name: "Table Reference Formatting",
        description: "Lowercase table citations inside parentheses: Table → table, Tab. → table (EPL style)",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles,
            existingSuggestions = [], rejectedSuggestions = []) {

            const staleIds = new Set();

            const { segments } = buildStyleContext({
                texFile, session, Range, anchorlist,
                existingSuggestions, staleIds,
                styleName: this.name,
                markerClass: this.markerClass,
                scope: { wholeDocument: true },
                excludeMath: true,
                overlapMode: 'simple',
            });

            const TAB_KEYS = ['Tables', 'Table', 'Tabs.', 'Tabs', 'Tab.', 'Tab'];
            const TAB_MAP = Object.fromEntries(TAB_KEYS.map(k => [k, _CITATION_KW_MAP[k]]));
            const TAB_PAT = _buildCitationPattern(TAB_KEYS);

            const triggerPat = /\(/g;

            for (const seg of segments) {
                triggerPat.lastIndex = 0;
                let m;
                while ((m = triggerPat.exec(seg.text)) !== null) {
                    const paren = _extractBalancedParen(seg.text, m.index);
                    if (!paren.endsWith(')')) continue;

                    if (!/\b(?:Tables?|Tabs?\.?)\b/.test(paren)) continue;

                    const suggestion = _applyCitationKeywords(paren, TAB_PAT, TAB_MAP);
                    if (paren === suggestion) continue;

                    seg.emit(paren, suggestion, m.index, this.description);
                }
            }

            return staleIds;
        },
    },
    // allRefStandardization: {
    //     name: "All Reference Formatting",
    //     description: "Lowercase all citation keywords inside parentheses: Figure→fig., Table→table, Eq.→eq. (EPL style)",
    //     markerClass: "ace-Common-highlight",

    //     execute(texFile, session, Range, anchorlist, fileName, Allfiles,
    //         existingSuggestions = [], rejectedSuggestions = []) {

    //         const staleIds = new Set();

    //         const { segments } = buildStyleContext({
    //             texFile, session, Range, anchorlist,
    //             existingSuggestions, staleIds,
    //             styleName: this.name,
    //             markerClass: this.markerClass,
    //             scope: { wholeDocument: true },
    //             excludeMath: true,
    //             overlapMode: 'simple',
    //         });

    //         // All keywords combined — sorted longest-first (already done in _CITATION_KW_SORTED)
    //         const ALL_PAT = _buildCitationPattern(_CITATION_KW_SORTED);

    //         // Quick pre-filter: paren must contain a citation keyword to be worth processing
    //         const KEYWORD_QUICK = /\b(?:Figures?|Figs?\.?|Tables?|Tabs?\.?|Equations?|Eqs?\.?)\b/;

    //         const triggerPat = /\(/g;

    //         for (const seg of segments) {
    //             triggerPat.lastIndex = 0;
    //             let m;
    //             while ((m = triggerPat.exec(seg.text)) !== null) {
    //                 const paren = _extractBalancedParen(seg.text, m.index);
    //                 if (!paren.endsWith(')')) continue;             // unbalanced

    //                 // Quick pre-filter: skip parens with no citation keyword
    //                 if (!KEYWORD_QUICK.test(paren)) continue;

    //                 // Apply all keyword replacements
    //                 const suggestion = _applyCitationKeywords(paren, ALL_PAT, _CITATION_KW_MAP);
    //                 if (paren === suggestion) continue;             // already correct

    //                 // Anchor: originalText = paren content, local offset = m.index
    //                 seg.emit(paren, suggestion, m.index, this.description);
    //             }
    //         }

    //         return staleIds;
    //     },
    // },
    DataAvailabilityStatement: {
        name: "Data Availability Statement",
        description: "Add a data availability statement before the bibliography",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist, fileName, Allfiles,
            existingSuggestions = [], rejectedSuggestions = []) {

            const staleIds = new Set();
            const self = this;

            // ── Emitter ───────────────────────────────────────────────────────────
            const emit = makeAnchorEmitter({
                texFile, session, Range, anchorlist,
                existingSuggestions, staleIds,
                styleName: self.name,
                markerClass: self.markerClass,
                overlapMode: 'skip-if-same', // if identical anchor already exists → keep it
            });

            // ── The three standard DAS options ────────────────────────────────────
            const DAS_OPTIONS = [
                {
                    label: 'Available on request',
                    text: 'Data availability statement: The data that support the findings of this study are available upon reasonable request from the authors.',
                },
                {
                    label: 'No new data',
                    text: 'Data availability statement: No new data were created or analysed in this study.',
                },
                {
                    label: 'Included in article',
                    text: 'Data availability statement: All data that support the findings of this study are included within the article (and any supplementary files).',
                },
            ];
            function hasDataStatement(bibAbsIndex) {
                const lookback = texFile.slice(Math.max(0, bibAbsIndex - 500), bibAbsIndex);
                return lookback.includes('Data availability statement:') ||
                    lookback.includes('Data Availability Statement:');
            }


            const bibPat = /\\begin\s*\{thebibliography\}\s*(?:\{[^}]*\})?/g;
            let m;

            while ((m = bibPat.exec(texFile)) !== null) {
                if (isCommented(texFile, m.index)) continue;

                const fullMatch = m[0];   // e.g. \begin{thebibliography}{99}
                const absIndex = m.index;

                if (hasDataStatement(absIndex)) continue;


                const multiSuggestions = DAS_OPTIONS.map(opt => ({
                    label: opt.label,
                    text: opt.text,
                    suggestionText: `${opt.text}\n${fullMatch}`,
                }));


                const defaultSuggestion = multiSuggestions[0].suggestionText;

                emit(
                    fullMatch,
                    defaultSuggestion,
                    absIndex,
                    self.description,
                    {
                        multiSuggestions,
                        isMultiSuggestion: true,
                        chosenIndex: 0,
                    }
                );
            }

            return staleIds;
        },
    },
    RemoveHiddenText: eplStyles.RemoveHiddenText,




}
console.log("the common styles object is :", eplStyles.RemoveHiddenText);